import { useState, useEffect, useLayoutEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useSearchParams } from "react-router-dom";
import { Menu, X, Heart, Calendar, Music, Users, Sparkles, MapPin, Clock, Phone, Mail, ChevronRight, Star, ArrowRight, CheckCircle2, Drum, Palette, Lock, Send, Eye, RefreshCw, CreditCard, Instagram, GraduationCap, Bell } from "lucide-react";

// Theme Context for color switching
const ThemeContext = createContext();

const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [colorTheme, setColorTheme] = useState("warm"); // "warm" or "jewel"
  
  const themes = {
    warm: {
      name: "Vibrant Warm",
      primary: "from-amber-400 to-orange-500",
      primarySolid: "bg-amber-400",
      primaryHover: "hover:bg-amber-500",
      accent: "from-orange-500 to-red-500",
      accentSolid: "bg-orange-500",
      text: "text-amber-50",
      textMuted: "text-amber-100/80",
      textAccent: "text-amber-400",
      cardBg: "bg-amber-950/40",
      cardBorder: "border-amber-500/30",
      navBg: "bg-stone-900/90",
      gradientHero: "from-amber-400 via-orange-400 to-red-500",
      buttonPrimary: "bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-stone-900",
      buttonSecondary: "border-amber-400 text-amber-300 hover:bg-amber-400/20",
      highlight: "bg-amber-400/20",
      donateGradient: "from-red-500 to-orange-500",
    },
    jewel: {
      name: "Rich Jewel",
      primary: "from-emerald-400 to-teal-500",
      primarySolid: "bg-emerald-500",
      primaryHover: "hover:bg-emerald-600",
      accent: "from-purple-500 to-fuchsia-500",
      accentSolid: "bg-purple-500",
      text: "text-emerald-50",
      textMuted: "text-emerald-100/80",
      textAccent: "text-emerald-400",
      cardBg: "bg-emerald-950/40",
      cardBorder: "border-emerald-500/30",
      navBg: "bg-stone-900/90",
      gradientHero: "from-emerald-400 via-teal-400 to-purple-500",
      buttonPrimary: "bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-stone-900",
      buttonSecondary: "border-emerald-400 text-emerald-300 hover:bg-emerald-400/20",
      highlight: "bg-emerald-400/20",
      donateGradient: "from-purple-500 to-fuchsia-500",
    }
  };

  const theme = themes[colorTheme];
  const toggleTheme = () => setColorTheme(colorTheme === "warm" ? "jewel" : "warm");

  return (
    <ThemeContext.Provider value={{ theme, colorTheme, toggleTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { Toaster, toast } from "sonner";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video URL for hero background
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/jrx9zx8t_copy_5D38E0F8-6BB2-4178-B68F-9502D5C40B79.mov";

// Additional videos
const ALL_VIDEOS = [
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/jrx9zx8t_copy_5D38E0F8-6BB2-4178-B68F-9502D5C40B79.mov",
    title: "Street Performance",
    description: "Community drum and dance performance"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/3heofv4x_b65c0088-9b60-4773-aca5-76fe6a288cee.mov",
    title: "Stage Performance",
    description: "Theatrical storytelling production"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/oj694mhu_74568871-a903-45f9-8eb2-1db921769afc.mov",
    title: "Performance Highlights",
    description: "Cultural Arts Theater showcase"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/9g6e13ee_5758a369-0012-4683-b539-ecc92e2974e1.mov",
    title: "Live Performance",
    description: "Drum and dance ensemble"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/d2x4lqw3_b3f19759-918f-4ab9-849f-1bb18b7d4ff2.mp4",
    title: "Youth Ensemble",
    description: "Young performers in action"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/4qifndm8_IMG_6496.mov",
    title: "Dance Performance",
    description: "Cultural dance showcase"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/ijp36a0s_IMG_4039.mov",
    title: "Drum Circle",
    description: "Community drumming session"
  }
];

const SECONDARY_VIDEO_URL = ALL_VIDEOS[1].url;

// Images from the organization - Original
const CAMP_IMAGES = [
  "https://customer-assets.emergentagent.com/job_youth-theater/artifacts/fvm4p3np_IMG_2833.jpeg",
  "https://customer-assets.emergentagent.com/job_youth-theater/artifacts/6d82vy8y_IMG_2199.jpeg",
  "https://customer-assets.emergentagent.com/job_youth-theater/artifacts/e2jirjz0_IMG_2832.jpeg",
  "https://customer-assets.emergentagent.com/job_youth-theater/artifacts/l3113mpu_IMG_2830.jpeg",
  "https://customer-assets.emergentagent.com/job_youth-theater/artifacts/518yjk2n_IMG_2235.jpeg"
];

// New uploaded images
const NEW_IMAGES = [
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/0k07dpmy_04a1123b-1dbf-42bd-a5e9-e630d7373631.jpeg", // Stage performance
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/k0889rj3_ebd043bc-bbf7-4808-93f7-4e6efbe3aa19.jpeg", // Drummers
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/uf52432n_1f3b4e6c-66a2-4b15-9011-e1c2467da958.jpeg", // Dancer in yellow
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/mqkj4ta6_6cc75d13-d373-4105-bfdd-a4f7cbae3901.jpeg", // Two drummers
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/s53d870f_922d2bfe-b327-4ec2-87cb-d12e9c7f5f12.jpeg", // Drummers on stage
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/k4scxj09_f4ff7494-575d-4690-a206-a69f6b02d815.jpeg", // Dancers on stage
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/rksacens_IMG_5255.jpeg", // Youth dancers in colorful outfits
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/azjoq2e2_IMG_0767.jpeg", // Adult drummers at event
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/lps66zrr_IMG_4051.jpeg", // Ensemble group photo
  "https://customer-assets.emergentagent.com/job_community-serve-1/artifacts/djlkrm97_IMG_4932.jpg"   // Outdoor hotel performance
];

// All gallery images combined
const ALL_IMAGES = [...CAMP_IMAGES, ...NEW_IMAGES];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { theme, colorTheme, toggleTheme } = useTheme();
  
  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/classes", label: "Classes" },
    { path: "/camp", label: "Summer Camp" },
    { path: "/theater-program", label: "Theater Program" },
    { path: "/gallery", label: "Gallery" },
    { path: "/book-event", label: "Book Us" },
    { path: "/donate", label: "Donate" },
    { path: "/contact", label: "Contact" }
  ];

  return (
    <nav className={`fixed w-full z-50 ${theme.navBg} backdrop-blur-md border-b ${theme.cardBorder}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${theme.primary} flex items-center justify-center shadow-lg`}>
              <Drum className="w-7 h-7 text-stone-900" />
            </div>
            <div className="hidden sm:block">
              <span className={`font-display text-xl font-bold ${theme.text}`}>Cultural Arts Theater Inc.</span>
              <p className={`text-xs ${theme.textMuted}`}>Tampa, Florida</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300
                  ${location.pathname === link.path 
                    ? `${theme.highlight} ${theme.textAccent}` 
                    : `${theme.textMuted} hover:${theme.text} hover:${theme.highlight}`}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`ml-4 p-2 rounded-full ${theme.highlight} ${theme.textAccent} hover:scale-110 transition-transform`}
              title={`Switch to ${colorTheme === 'warm' ? 'Jewel' : 'Warm'} theme`}
            >
              <Palette size={20} />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 ${theme.textAccent}`}
              title="Switch theme"
            >
              <Palette size={20} />
            </button>
            <button
              className={`p-2 ${theme.textMuted}`}
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className={`lg:hidden ${theme.navBg} border-t ${theme.cardBorder}`}>
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-all
                  ${location.pathname === link.path 
                    ? `${theme.highlight} ${theme.textAccent}` 
                    : `${theme.textMuted} hover:${theme.highlight}`}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const { theme } = useTheme();
  
  return (
    <footer className={`bg-stone-950 border-t ${theme.cardBorder}`}>
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${theme.primary} flex items-center justify-center`}>
                <Drum className="w-7 h-7 text-stone-900" />
              </div>
              <span className={`font-display text-2xl font-bold ${theme.text}`}>Cultural Arts Theater Inc.</span>
            </div>
            <p className={`${theme.textMuted} mb-6 max-w-md`}>
              A 501(c)(3) nonprofit dedicated to preserving and teaching African drumming, dance, and storytelling.
            </p>
            <div className={`flex items-center gap-2 ${theme.textMuted}`}>
              <MapPin size={16} />
              <span>Tampa, Florida</span>
            </div>
          </div>
          
          <div>
            <h4 className={`font-semibold ${theme.text} mb-4`}>Quick Links</h4>
            <ul className={`space-y-2 ${theme.textMuted}`}>
              <li><Link to="/classes" className={`hover:${theme.textAccent} transition-colors`}>Classes</Link></li>
              <li><Link to="/camp" className={`hover:${theme.textAccent} transition-colors`}>Summer Camp</Link></li>
              <li><Link to="/book-event" className={`hover:${theme.textAccent} transition-colors`}>Book a Performance</Link></li>
              <li><Link to="/donate" className={`hover:${theme.textAccent} transition-colors`}>Donate</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className={`font-semibold ${theme.text} mb-4`}>Contact</h4>
            <ul className={`space-y-2 ${theme.textMuted}`}>
              <li className="flex items-center gap-2">
                <MapPin size={16} />
                <span>Tampa, Florida</span>
              </li>
              <li>
                <Link to="/contact" className={`hover:${theme.textAccent} transition-colors`}>Send us a message</Link>
              </li>
            </ul>
          </div>
        </div>
        
        <div className={`border-t ${theme.cardBorder} mt-12 pt-8`} data-testid="footer-supporters-section">
          <h4 className={`font-semibold ${theme.text} mb-4 text-center`}>Our Supporters</h4>
          <div className="flex flex-wrap justify-center items-stretch gap-4">
            <a href="https://www.childrensboard.org" target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-4 inline-flex items-center hover:opacity-90 transition-opacity" data-testid="footer-childrens-board-logo">
              <img src="/images/childrens-board-logo.png" alt="Children's Board Hillsborough County" className="h-14 w-auto" />
            </a>
            <a href="https://www.stepupforstudents.org" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity" data-testid="footer-step-up-badge">
              <StepUpBadge />
            </a>
          </div>
          <p className={`${theme.textMuted} text-sm text-center mt-4 max-w-2xl mx-auto`} data-testid="footer-childrens-board-text">
            Cultural Arts Theater, Inc. gratefully recognizes the Children's Board of Hillsborough County for supporting our organizational capacity-building efforts through the Technical Assistance Grant.
          </p>
          <p className={`${theme.textMuted} text-sm text-center mt-3 max-w-2xl mx-auto`} data-testid="footer-step-up-text">
            We are an official Step Up For Students provider.
          </p>
        </div>
        
        <div className={`border-t ${theme.cardBorder} mt-8 pt-8 text-center ${theme.textMuted}`}>
          <p>&copy; {new Date().getFullYear()} Cultural Arts Theater. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Flyer with scroll-triggered pop reveal
const FlyerShowcase = () => {
  const { theme } = useTheme();
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.intersectionRatio >= 0.5) setRevealed(true);
      else if (e.intersectionRatio <= 0) setRevealed(false);
    }, { threshold: [0, 0.5] });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  
  const burstColors = ["#fbbf24", "#f43f5e", "#22c55e", "#38bdf8", "#f97316"];
  
  return (
    <div ref={ref} className="order-1 lg:order-2 max-w-md mx-auto lg:ml-auto w-full relative py-6 px-4">
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.primary} opacity-30 blur-3xl rounded-full animate-pulse`} />
      {revealed && [...Array(12)].map((_, i) => (
        <span
          key={i}
          className="burst-particle"
          style={{ "--angle": `${i * 30}deg`, "--delay": `${0.4 + (i % 4) * 0.05}s`, backgroundColor: burstColors[i % 5] }}
        />
      ))}
      <div className={revealed ? "flyer-pop" : "flyer-hidden"}>
        <div className="relative flyer-float">
          <div className="absolute -top-5 -right-4 z-10 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-display font-bold text-lg px-5 py-2 rounded-full shadow-xl rotate-6">
            We're Back!
          </div>
          <div className="absolute -bottom-4 -left-3 z-10 bg-amber-400 text-stone-900 font-bold text-sm px-4 py-1.5 rounded-full shadow-xl -rotate-6">
            Every Tuesday
          </div>
          <img 
            src="/images/drum-dance-flyer.jpg" 
            alt="African Drum & Dance Classes flyer - Every Tuesday starting 8/25" 
            className="w-full h-auto rounded-3xl shadow-2xl ring-4 ring-amber-500/40 -rotate-2 hover:rotate-0 hover:scale-[1.03] transition-transform duration-500"
            data-testid="class-flyer-image"
          />
        </div>
      </div>
    </div>
  );
};

// Unsubscribe Page (class reminder opt-out)
const UnsubscribePage = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState("loading");
  
  const act = async (action) => {
    try {
      await axios.post(`${API}/classes/unsubscribe`, { email, token, action });
      setStatus(action === "resubscribe" ? "resubscribed" : "unsubscribed");
    } catch (error) {
      setStatus("error");
    }
  };
  
  useEffect(() => {
    if (email && token) act("unsubscribe");
    else setStatus("error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="unsubscribe-page">
      <Card className={`${theme.cardBg} border ${theme.cardBorder} w-full max-w-md text-center`}>
        <CardContent className="p-8">
          {status === "loading" && (
            <>
              <RefreshCw className={`w-10 h-10 ${theme.textAccent} animate-spin mx-auto mb-4`} />
              <p className={theme.textMuted}>Updating your preferences...</p>
            </>
          )}
          {status === "unsubscribed" && (
            <div data-testid="unsubscribe-success">
              <CheckCircle2 className={`w-12 h-12 ${theme.textAccent} mx-auto mb-4`} />
              <h1 className={`font-display text-2xl font-bold ${theme.text} mb-2`}>You're Unsubscribed</h1>
              <p className={`${theme.textMuted} mb-6`}>
                {email} will no longer receive Monday class reminder emails. We'd still love to see you on Tuesdays!
              </p>
              <Button onClick={() => act("resubscribe")} className={`${theme.buttonPrimary} rounded-full px-6`} data-testid="resubscribe-btn">
                <Bell className="w-4 h-4 mr-2" /> Resubscribe Me
              </Button>
            </div>
          )}
          {status === "resubscribed" && (
            <div data-testid="resubscribe-success">
              <Bell className={`w-12 h-12 ${theme.textAccent} mx-auto mb-4`} />
              <h1 className={`font-display text-2xl font-bold ${theme.text} mb-2`}>Welcome Back!</h1>
              <p className={theme.textMuted}>You'll get class reminder emails again every Monday. See you Tuesday!</p>
            </div>
          )}
          {status === "error" && (
            <div data-testid="unsubscribe-error">
              <X className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h1 className={`font-display text-2xl font-bold ${theme.text} mb-2`}>Invalid Link</h1>
              <p className={theme.textMuted}>This unsubscribe link is invalid or expired. Please use the link from your latest reminder email, or contact us.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      html.style.scrollBehavior = prev;
    });
  }, [pathname]);
  return null;
};

// Wesley Chapel Coming Soon + Pre-Registration
const WesleyChapelSection = () => {
  const { theme } = useTheme();
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "Both" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    axios.get(`${API}/classes/wesley-chapel/count`)
      .then((res) => setCount(res.data.count || 0))
      .catch(() => {});
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/classes/wesley-chapel/preregister`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        interest: form.interest
      });
      setDone(true);
      setCount((c) => c + 1);
      toast.success("You're pre-registered! We'll email you when Wesley Chapel classes launch.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <section className="py-20 bg-stone-900" data-testid="wesley-chapel-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className={`relative rounded-3xl overflow-hidden border-2 border-dashed ${theme.cardBorder} bg-stone-950 p-8 lg:p-12`}>
          <div className={`absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br ${theme.primary} opacity-20 blur-3xl rounded-full`} />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 text-sm font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide animate-pulse">
                <Sparkles className="w-4 h-4" /> Coming Soon
              </span>
              <h2 className={`font-display text-3xl sm:text-4xl font-bold ${theme.text} mb-3`}>
                Drum & Dance Classes Are Coming to Wesley Chapel!
              </h2>
              <p className={`${theme.textMuted} text-lg mb-4`}>
                We're bringing the rhythm north! Cultural Arts Theater is planning weekly African drum and dance classes in the Wesley Chapel area.
              </p>
              <div className={`flex items-center gap-2 ${theme.textAccent} font-semibold mb-4`}>
                <MapPin className="w-5 h-5" /> Wesley Chapel, FL — location & dates announced soon
              </div>
              <p className={`${theme.textMuted}`}>
                Pre-register below (it's free, no commitment) so you're first in line when we launch — the more interest we see, the faster classes start!
              </p>
              {count >= 3 && (
                <div className={`mt-4 inline-flex items-center gap-2 bg-gradient-to-r ${theme.primary} text-stone-900 font-bold px-4 py-2 rounded-full`} data-testid="wesley-counter">
                  <Users className="w-4 h-4" /> Join {count}+ neighbors already signed up!
                </div>
              )}
            </div>
            <div>
              {done ? (
                <div className={`${theme.cardBg} border ${theme.cardBorder} rounded-2xl p-8 text-center`} data-testid="wesley-success">
                  <CheckCircle2 className={`w-12 h-12 ${theme.textAccent} mx-auto mb-3`} />
                  <p className={`${theme.text} font-semibold text-lg`}>You're on the Wesley Chapel list!</p>
                  <p className={theme.textMuted}>We'll email you as soon as classes are ready to launch. Spread the word — it helps us start sooner!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className={`${theme.cardBg} border ${theme.cardBorder} rounded-2xl p-6 space-y-4`} data-testid="wesley-form">
                  <h3 className={`font-display text-xl font-bold ${theme.text}`}>Pre-Register — Free & No Commitment</h3>
                  <Input
                    required
                    placeholder="Your name *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-stone-700/50 border-amber-900/30 text-amber-100 placeholder:text-stone-400"
                    data-testid="wesley-name"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      type="email"
                      required
                      placeholder="Email *"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-stone-700/50 border-amber-900/30 text-amber-100 placeholder:text-stone-400"
                      data-testid="wesley-email"
                    />
                    <Input
                      placeholder="Phone (optional)"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-stone-700/50 border-amber-900/30 text-amber-100 placeholder:text-stone-400"
                      data-testid="wesley-phone"
                    />
                  </div>
                  <div>
                    <p className={`${theme.textMuted} text-sm mb-2`}>I'm interested in:</p>
                    <div className="flex gap-2">
                      {["Drum", "Dance", "Both"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setForm({ ...form, interest: opt })}
                          data-testid={`wesley-interest-${opt.toLowerCase()}`}
                          className={`flex-1 py-2 rounded-full font-semibold text-sm transition-all ${
                            form.interest === opt
                              ? `bg-gradient-to-r ${theme.primary} text-stone-900`
                              : `bg-stone-700/50 ${theme.textMuted} border ${theme.cardBorder}`
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" disabled={submitting} className={`w-full ${theme.buttonPrimary} rounded-full py-5`} data-testid="wesley-submit-btn">
                    {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-2" /> Pre-Register Me</>}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== PAGE COMPONENTS ==============

// Step Up For Students Provider Badge
const StepUpBadge = ({ large = false }) => (
  <div className={`inline-flex flex-col items-center bg-white rounded-xl ${large ? 'p-4' : 'p-3'}`} data-testid="step-up-badge">
    <img src="/images/step-up-logo.webp" alt="Step Up For Students" className={`${large ? 'h-20' : 'h-14'} w-auto`} />
    <p className={`text-stone-700 font-semibold ${large ? 'text-sm' : 'text-xs'} mt-1`}>Official Provider</p>
  </div>
);

// Live Countdown to Class
const ClassCountdown = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  const firstClass = new Date("2026-08-25T18:30:00-04:00").getTime();
  const week = 7 * 24 * 3600 * 1000;
  let label = "First class starts in";
  let target = firstClass;
  if (now >= firstClass) {
    label = "Next class starts in";
    target = firstClass + Math.ceil((now - firstClass) / week) * week;
  }
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (24 * 3600 * 1000));
  const hours = Math.floor((diff / (3600 * 1000)) % 24);
  const mins = Math.floor((diff / 60000) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  
  return (
    <div data-testid="class-countdown">
      <p className="text-amber-400 font-semibold tracking-wider text-xs uppercase mb-2">{label}</p>
      <div className="flex gap-3">
        {[{ v: days, l: "Days" }, { v: hours, l: "Hrs" }, { v: mins, l: "Min" }, { v: secs, l: "Sec" }].map((unit, i) => (
          <div key={i} className="bg-stone-900 border border-amber-500/30 rounded-xl px-4 py-3 text-center min-w-[68px] shadow-lg">
            <p className="font-display text-3xl font-bold text-amber-400 tabular-nums">{String(unit.v).padStart(2, "0")}</p>
            <p className="text-stone-400 text-xs uppercase tracking-wide">{unit.l}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Camp 2027 Notify Me Form
const CampNotifyForm = () => {
  const { theme } = useTheme();
  const [form, setForm] = useState({ email: "", parent_name: "", child_age: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/camp/notify`, {
        email: form.email,
        parent_name: form.parent_name || null,
        child_age: form.child_age || null
      });
      setDone(true);
      toast.success("You're on the list! We'll email you when registration opens.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  if (done) {
    return (
      <div className="text-center py-4" data-testid="camp-notify-success">
        <CheckCircle2 className={`w-12 h-12 ${theme.textAccent} mx-auto mb-3`} />
        <p className={`${theme.text} font-semibold text-lg`}>You're on the list!</p>
        <p className={theme.textMuted}>We'll email you the moment Summer Camp 2027 registration opens.</p>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="camp-notify-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          type="text"
          placeholder="Parent/Guardian name"
          value={form.parent_name}
          onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 placeholder:text-stone-400"
          data-testid="notify-parent-name"
        />
        <Input
          type="text"
          placeholder="Child's age (optional)"
          value={form.child_age}
          onChange={(e) => setForm({ ...form, child_age: e.target.value })}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 placeholder:text-stone-400"
          data-testid="notify-child-age"
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          type="email"
          required
          placeholder="Your email address *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 placeholder:text-stone-400 flex-1"
          data-testid="notify-email"
        />
        <Button type="submit" disabled={submitting} className={`${theme.buttonPrimary} rounded-full px-6`} data-testid="notify-submit-btn">
          {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-2" /> Notify Me</>}
        </Button>
      </div>
    </form>
  );
};

// Home Page
const HomePage = () => {
  const { theme, colorTheme } = useTheme();
  
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={CAMP_IMAGES[0]}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.4)' }}
        >
          <source src={HERO_VIDEO_URL} type="video/quicktime" />
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
        
        {/* Fallback image if video doesn't load */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${CAMP_IMAGES[0]})`,
            filter: 'brightness(0.4)',
            zIndex: -1
          }}
        />
        
        {/* Gradient Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-b from-stone-900/60 via-transparent to-stone-900`} />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center pt-24">
          <div className={`inline-flex items-center gap-2 ${theme.highlight} border ${theme.cardBorder} rounded-full px-4 py-2 mb-8 backdrop-blur-sm`}>
            <Sparkles className={`w-4 h-4 ${theme.textAccent}`} />
            <span className={`text-sm ${theme.textMuted}`}>501(c)(3) Nonprofit Organization</span>
          </div>
          
          <h1 className={`font-display text-5xl sm:text-6xl lg:text-7xl font-bold ${theme.text} mb-6 leading-tight`}>
            Where Culture,<br />
            <span className={`text-transparent bg-clip-text bg-gradient-to-r ${theme.gradientHero}`}>
              Creativity & Community
            </span><br />
            Come Together
          </h1>
          
          <p className={`text-lg sm:text-xl ${theme.textMuted} max-w-2xl mx-auto mb-10`}>
            Preserving and teaching African drumming, dance, and storytelling as powerful tools for education, self-expression, and community connection.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/classes">
              <Button 
                size="lg" 
                className={`${theme.buttonPrimary} rounded-full px-8 py-6 text-lg font-semibold shadow-xl`}
                data-testid="hero-classes-btn"
              >
                <Calendar className="mr-2" /> Attend a Class
              </Button>
            </Link>
            <Link to="/book-event">
              <Button 
                size="lg" 
                variant="outline" 
                className={`${theme.buttonSecondary} border-2 rounded-full px-8 py-6 text-lg`}
                data-testid="hero-book-btn"
              >
                <Music className="mr-2" /> Book a Performance
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className={`w-8 h-8 ${theme.textAccent} rotate-90`} />
        </div>
      </section>

      {/* New Classes Launch Announcement */}
      <section className="py-20 bg-stone-950 relative overflow-hidden" data-testid="class-launch-banner">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className={`inline-block bg-gradient-to-r ${theme.primary} text-stone-900 text-sm font-bold px-5 py-2 rounded-full mb-5 uppercase tracking-wide animate-pulse`}>
                We're Back — Every Tuesday Starting 8/25
              </span>
              <h2 className={`font-display text-4xl sm:text-5xl font-bold ${theme.text} mb-4`}>
                Community Drum & Dance Classes Are Back!
              </h2>
              <p className={`${theme.textMuted} text-lg mb-6`}>
                Feel it. Live it. Be it. Join us every Tuesday for African drum and dance classes — open to the community, all ages, all levels, all welcome! In collaboration with Kuumba & Cultural Arts Theater.
              </p>
              <div className="mb-8">
                <ClassCountdown />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-4`}>
                  <Clock className={`w-5 h-5 ${theme.textAccent} mb-2`} />
                  <p className={`${theme.text} font-semibold`}>Drum Class: 6:30 PM</p>
                  <p className={`${theme.text} font-semibold`}>Dance Class: 7:30 PM</p>
                  <p className={`${theme.textMuted} text-sm mt-1`}>Every Tuesday starting August 25</p>
                </div>
                <div className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-4`}>
                  <MapPin className={`w-5 h-5 ${theme.textAccent} mb-2`} />
                  <p className={`${theme.text} font-semibold`}>THJCA</p>
                  <p className={`${theme.textMuted} text-sm`}>2005 N Lamar Ave, Tampa, FL 33602</p>
                </div>
              </div>
              <div className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-4 mb-6`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span className={theme.textMuted}><strong className={theme.text}>$10</strong> Children 10 & under</span>
                  <span className={theme.textMuted}><strong className={theme.text}>$15</strong> Adults</span>
                  <span className={theme.textMuted}><strong className={theme.text}>$25</strong> Drum + Dance (both classes)</span>
                </div>
                <p className={`${theme.textMuted} text-sm mt-2`}>Ask about monthly rates & group rates for 4+!</p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/classes">
                  <Button size="lg" className={`${theme.buttonPrimary} rounded-full px-8`} data-testid="banner-register-btn">
                    Register for Classes <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:8138470014" className={`flex items-center gap-2 ${theme.textAccent} font-semibold`} data-testid="banner-contact-phone">
                  <Phone size={18} /> Contact Aiyetora: 813-847-0014
                </a>
              </div>
            </div>
            <FlyerShowcase />
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 bg-stone-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className={`font-display text-4xl sm:text-5xl font-bold ${theme.text} mb-4`}>What We Offer</h2>
            <p className={`${theme.textMuted} max-w-2xl mx-auto`}>Building confidence, creativity, and cultural awareness through the performing arts</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Drum, title: "Drum & Dance Classes", desc: "Weekly community classes for all ages and skill levels", link: "/classes" },
              { icon: Calendar, title: "Summer Camp", desc: "4-week immersive arts experience for youth ages 5-18", link: "/camp" },
              { icon: Star, title: "Year-Long Program", desc: "Audition-based theater program with live performances", link: "/theater-program" },
              { icon: Music, title: "Performances", desc: "Book our ensembles for your events and celebrations", link: "/book-event" }
            ].map((item, i) => (
              <Link key={i} to={item.link}>
                <Card className={`group ${theme.cardBg} ${theme.cardBorder} border hover:border-opacity-100 transition-all duration-300 h-full hover:-translate-y-2 hover:shadow-2xl`}>
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${theme.primary} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <item.icon className="w-7 h-7 text-stone-900" />
                    </div>
                    <h3 className={`font-display text-xl font-semibold ${theme.text} mb-2`}>{item.title}</h3>
                    <p className={`${theme.textMuted} text-sm`}>{item.desc}</p>
                    <div className={`mt-4 flex items-center ${theme.textAccent} text-sm font-medium`}>
                      Learn more <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className={`py-24 ${theme.cardBg}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className={`${theme.textAccent} font-semibold tracking-wider text-sm uppercase`}>Our Mission</span>
              <h2 className={`font-display text-4xl sm:text-5xl font-bold ${theme.text} mt-2 mb-6`}>
                Empowering Communities Through Cultural Arts
              </h2>
              <p className={`${theme.textMuted} text-lg mb-6`}>
                Cultural Arts Theater provides youth, families, and communities—especially those from underserved backgrounds—with access to high-quality performing arts education.
              </p>
              <ul className="space-y-4 mb-8">
                {["Builds confidence and creativity", "Develops discipline and leadership", "Fosters cultural awareness and pride", "Creates inclusive community spaces"].map((item, i) => (
                  <li key={i} className={`flex items-center gap-3 ${theme.textMuted}`}>
                    <CheckCircle2 className={`w-5 h-5 ${theme.textAccent} flex-shrink-0`} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button className={`${theme.buttonPrimary} rounded-full px-6`} data-testid="learn-more-btn">
                  Learn More About Us <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {CAMP_IMAGES.slice(0, 4).map((img, i) => (
                <div 
                  key={i} 
                  className={`rounded-2xl overflow-hidden ${i === 0 ? 'col-span-2' : ''} shadow-xl`}
                >
                  <img 
                    src={img} 
                    alt="Cultural Arts Theater performance" 
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Community Classes Highlight */}
      <section className="py-24 bg-stone-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className={`bg-gradient-to-br ${theme.primary} rounded-3xl overflow-hidden shadow-2xl`}>
            <div className="grid lg:grid-cols-2">
              <div className="p-8 lg:p-12">
                <span className="inline-block bg-stone-900/20 text-stone-900 text-sm font-medium px-4 py-1 rounded-full mb-4">
                  Every Tuesday — Starting August 25
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
                  Community Drum & Dance Classes
                </h2>
                <p className="text-stone-800 mb-6">
                  Join us for weekly classes where all ages and skill levels are welcome. Experience the joy of traditional African rhythms and movement!
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="bg-stone-900/20 rounded-xl p-4">
                    <Clock className="w-5 h-5 text-stone-800 mb-2" />
                    <p className="text-stone-900 font-semibold">Drum Class: 6:30 PM</p>
                    <p className="text-stone-800 text-sm">Dance Class: 7:30 PM</p>
                  </div>
                  <div className="bg-stone-900/20 rounded-xl p-4">
                    <MapPin className="w-5 h-5 text-stone-800 mb-2" />
                    <p className="text-stone-900 font-semibold">THJCA — 2005 N Lamar Ave</p>
                    <p className="text-stone-800 text-sm">Tampa, FL 33602</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
                  <span className="text-3xl font-bold text-stone-900">$15</span>
                  <span className="text-stone-800">adult</span>
                  <span className="text-stone-700">|</span>
                  <span className="text-3xl font-bold text-stone-900">$10</span>
                  <span className="text-stone-800">child (10 & under)</span>
                  <span className="text-stone-700">|</span>
                  <span className="text-3xl font-bold text-stone-900">$25</span>
                  <span className="text-stone-800">both classes</span>
                </div>
                <Link to="/classes">
                  <Button 
                    size="lg" 
                    className="bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8"
                    data-testid="register-class-btn"
                  >
                    Register Now
                  </Button>
                </Link>
              </div>
              <div 
                className="hidden lg:block"
                style={{
                  backgroundImage: `url(${CAMP_IMAGES[1]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Wesley Chapel Coming Soon */}
      <WesleyChapelSection />

      {/* Instagram Section */}
      <section className="py-24 bg-stone-950" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-fuchsia-600 via-rose-500 to-amber-500 shadow-2xl">
            <div className="grid lg:grid-cols-2 items-center">
              <div className="p-8 lg:p-14">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-6">
                  <Instagram className="w-9 h-9 text-white" />
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
                  See the Rhythm in Action
                </h2>
                <p className="text-white/90 text-lg mb-2">
                  Follow us on Instagram for class highlights, performance clips, event announcements, and behind-the-scenes moments.
                </p>
                <p className="text-white font-bold text-xl mb-8">@cultural.arts.theater</p>
                <a 
                  href="https://www.instagram.com/cultural.arts.theater" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="instagram-follow-btn"
                >
                  <Button size="lg" className="bg-white text-rose-600 hover:bg-white/90 rounded-full px-8 font-bold">
                    <Instagram className="mr-2 w-5 h-5" /> Follow Us on Instagram
                  </Button>
                </a>
              </div>
              <div className="hidden lg:grid grid-cols-2 gap-3 p-8">
                {ALL_IMAGES.slice(2, 6).map((img, i) => (
                  <a 
                    key={i} 
                    href="https://www.instagram.com/cultural.arts.theater" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`aspect-square rounded-2xl overflow-hidden shadow-xl ${i % 2 === 0 ? 'rotate-2' : '-rotate-2'} hover:rotate-0 transition-transform duration-300`}
                  >
                    <img src={img} alt="Cultural Arts Theater on Instagram" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-24 bg-stone-950">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Heart className={`w-12 h-12 mx-auto mb-6 ${colorTheme === 'warm' ? 'text-red-400' : 'text-fuchsia-400'}`} />
          <h2 className={`font-display text-4xl sm:text-5xl font-bold ${theme.text} mb-4`}>
            Support Our Mission
          </h2>
          <p className={`${theme.textMuted} text-lg mb-8 max-w-2xl mx-auto`}>
            Your donation helps provide scholarships, sustain year-round programs, and bring arts education to underserved communities in Tampa.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button 
                size="lg" 
                className={`bg-gradient-to-r ${theme.donateGradient} hover:opacity-90 text-white rounded-full px-8`}
                data-testid="donate-now-btn"
              >
                <Heart className="mr-2" /> Donate Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className={`${theme.buttonSecondary} border-2 rounded-full px-8`}
              >
                Partner With Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => (
  <div className="min-h-screen pt-20" data-testid="about-page">
    <section className="py-24 bg-gradient-to-br from-stone-900 to-amber-950/30">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-amber-400 font-semibold tracking-wider text-sm uppercase">About Us</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 mt-2">
            Our Story & Mission
          </h1>
        </div>
        
        <div className="prose prose-lg prose-invert max-w-none">
          <div className="bg-stone-800/50 rounded-3xl p-8 lg:p-12 border border-amber-900/30">
            <p className="text-xl text-amber-100/90 leading-relaxed mb-6">
              <strong className="text-amber-300">Cultural Arts Theater</strong> is a 501(c)(3) nonprofit organization dedicated to preserving, celebrating, and teaching African drumming, dance, and storytelling as powerful tools for education, self-expression, and community connection.
            </p>
            
            <p className="text-amber-200/80 leading-relaxed mb-6">
              Rooted in African cultural traditions, our programs provide youth, families, and communities—especially those from underserved backgrounds—with access to high-quality performing arts education that builds <strong className="text-amber-200">confidence, creativity, discipline, leadership, and cultural awareness</strong>.
            </p>
            
            <p className="text-amber-200/80 leading-relaxed mb-8">
              While African drum and dance remain the heart of our work, Cultural Arts Theater also incorporates complementary movement and performance styles to support well-rounded artistic development. Through classes, camps, performances, and theatrical productions, we create spaces where <em className="text-amber-300">culture, creativity, and community come together</em>.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mt-12">
              {[
                { title: "Youth Development", desc: "Building the next generation of artists and leaders" },
                { title: "Community Access", desc: "Making arts education available to all" },
                { title: "Cultural Preservation", desc: "Honoring African traditions through performance" },
                { title: "Artistic Excellence", desc: "High-quality training and productions" }
              ].map((item, i) => (
                <div key={i} className="bg-amber-900/20 rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-amber-200 mb-2">{item.title}</h3>
                  <p className="text-amber-200/60 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Supporters */}
        <div className="mt-12 bg-stone-800/50 rounded-3xl p-8 lg:p-12 border border-amber-900/30 text-center" data-testid="about-supporters-section">
          <span className="text-amber-400 font-semibold tracking-wider text-sm uppercase">Our Supporters</span>
          <h2 className="font-display text-3xl font-bold text-amber-50 mt-2 mb-6">Thank You to Our Partners</h2>
          <div className="flex flex-wrap justify-center items-stretch gap-4">
            <a href="https://www.childrensboard.org" target="_blank" rel="noopener noreferrer" className="bg-white rounded-2xl p-6 inline-flex items-center hover:opacity-90 transition-opacity" data-testid="about-childrens-board-logo">
              <img src="/images/childrens-board-logo.png" alt="Children's Board Hillsborough County" className="h-20 w-auto mx-auto" />
            </a>
            <a href="https://www.stepupforstudents.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-90 transition-opacity" data-testid="about-step-up-badge">
              <StepUpBadge large />
            </a>
          </div>
          <p className="text-amber-200/70 mt-6 max-w-2xl mx-auto">
            Cultural Arts Theater, Inc. gratefully recognizes the Children's Board of Hillsborough County for supporting our organizational capacity-building efforts through the Technical Assistance Grant.
          </p>
          <p className="text-amber-200/70 mt-4 max-w-2xl mx-auto">
            We are proud to be an official <strong className="text-amber-200">Step Up For Students</strong> provider.
          </p>
        </div>
      </div>
    </section>
  </div>
);

// Classes Page
const ClassesPage = () => {
  const [activeTab, setActiveTab] = useState("community");
  
  return (
    <div className="min-h-screen pt-20" data-testid="classes-page">
      <section className="py-24 bg-gradient-to-br from-stone-900 to-amber-950/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-400 font-semibold tracking-wider text-sm uppercase">Programs</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 mt-2 mb-4">
              Classes & Programs
            </h1>
            <p className="text-amber-200/60 max-w-2xl mx-auto">Year-round arts education for all ages and skill levels</p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {[
              { id: "community", label: "Community Classes" },
              { id: "homeschool", label: "Homeschool Classes" },
              { id: "corporate", label: "Groups & Corporate" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-amber-500 text-stone-900"
                    : "bg-stone-800/50 text-amber-200 hover:bg-stone-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Community Classes */}
          {activeTab === "community" && (
            <div className="space-y-8">
              <Card className="bg-gradient-to-br from-amber-600 to-orange-700 border-0 overflow-hidden">
                <CardContent className="p-8 lg:p-12">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div>
                      <h2 className="font-display text-3xl font-bold text-white mb-4">Community Drum & Dance Classes</h2>
                      <p className="text-amber-100/90 mb-6">
                        Join our welcoming community for weekly African drumming and dance instruction. All ages and skill levels are welcome!
                      </p>
                      
                      <div className="space-y-4 mb-8">
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 text-amber-200 mt-1" />
                          <div>
                            <p className="text-white font-semibold">Every Tuesday — Starting August 25!</p>
                            <p className="text-amber-100/70 text-sm">Drum: 6:30 PM | Dance: 7:30 PM</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-amber-200 mt-1" />
                          <div>
                            <p className="text-white font-semibold">THJCA — 2005 N Lamar Ave</p>
                            <p className="text-amber-100/70 text-sm">Tampa, FL 33602</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 text-amber-200 mt-1" />
                          <div>
                            <p className="text-white font-semibold">Contact Aiyetora</p>
                            <a href="tel:8138470014" className="text-amber-100/70 text-sm hover:text-white" data-testid="classes-contact-phone">813-847-0014</a>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/10 rounded-xl p-4 mb-6">
                        <h4 className="text-white font-semibold mb-2">Pricing</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          <div>
                            <span className="text-2xl font-bold text-white">$10</span>
                            <span className="text-amber-100/80 text-sm ml-2">Child (10 & under)</span>
                          </div>
                          <div>
                            <span className="text-2xl font-bold text-white">$15</span>
                            <span className="text-amber-100/80 text-sm ml-2">Adult</span>
                          </div>
                          <div>
                            <span className="text-2xl font-bold text-white">$25</span>
                            <span className="text-amber-100/80 text-sm ml-2">Drum + Dance (both)</span>
                          </div>
                        </div>
                        <p className="text-amber-100/70 text-sm mt-2">Ask about monthly rates & group rates for 4+!</p>
                        <p className="text-amber-100/70 text-sm mt-1">In collaboration with Kuumba & Cultural Arts Theater</p>
                      </div>

                      <div className="flex items-center gap-3 bg-white/10 rounded-xl p-3 mb-6" data-testid="classes-step-up-note">
                        <GraduationCap className="w-6 h-6 text-white flex-shrink-0" />
                        <p className="text-white text-sm font-medium">We are an official <strong>Step Up For Students</strong> provider!</p>
                      </div>
                    </div>

                    <CommunityClassForm />
                  </div>
                </CardContent>
              </Card>

              {/* Official Class Flyer */}
              <div className="max-w-md mx-auto">
                <img 
                  src="/images/drum-dance-flyer.jpg" 
                  alt="African Drum & Dance Classes flyer - Every Tuesday starting 8/25 at THJCA" 
                  className="w-full h-auto rounded-3xl shadow-2xl border border-amber-900/30"
                  data-testid="classes-page-flyer"
                />
              </div>
            </div>
          )}

          {/* Homeschool Classes */}
          {activeTab === "homeschool" && (
            <Card className="bg-stone-800/50 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-display text-2xl text-amber-100">Private Drum & Dance Classes for Homeschoolers</CardTitle>
                <CardDescription className="text-amber-200/60">
                  Bring the performing arts to your homeschool group with customized instruction
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-4 bg-amber-900/20 rounded-xl p-4 mb-6" data-testid="homeschool-step-up-badge">
                  <a href="https://www.stepupforstudents.org" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                    <StepUpBadge />
                  </a>
                  <p className="text-amber-100/90 text-sm flex-1 min-w-[200px]">
                    We are an official <strong className="text-amber-200">Step Up For Students</strong> provider — homeschool families can use their scholarship funds for our classes!
                  </p>
                </div>
                <HomeschoolForm />
              </CardContent>
            </Card>
          )}

          {/* Corporate/Groups */}
          {activeTab === "corporate" && (
            <Card className="bg-stone-800/50 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-display text-2xl text-amber-100">Private Classes for Groups & Corporate Events</CardTitle>
                <CardDescription className="text-amber-200/60">
                  Team building, corporate wellness, and group experiences through rhythm and movement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CorporateForm />
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Wesley Chapel Coming Soon */}
      <WesleyChapelSection />
    </div>
  );
};

// Summer Camp Page
const SummerCampPage = () => {
  const { theme } = useTheme();
  
  return (
    <div className="min-h-screen pt-20" data-testid="camp-page">
    {/* Hero */}
    <section className="relative py-24 overflow-hidden">
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(${NEW_IMAGES[0]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className={`absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-900/95 to-stone-900/80`} />
      
      <div className="relative max-w-6xl mx-auto px-4">
        <div className={`inline-flex items-center gap-2 ${theme.highlight} border ${theme.cardBorder} rounded-full px-4 py-2 mb-6`}>
          <Sparkles className={`w-4 h-4 ${theme.textAccent}`} />
          <span className={`text-sm ${theme.textMuted}`}>Summer 2026 Season Complete — Save the Date for 2027!</span>
        </div>
        
        <h1 className={`font-display text-4xl sm:text-5xl lg:text-6xl font-bold ${theme.text} mb-4`}>
          Summer Camp
        </h1>
        <p className={`text-xl ${theme.textMuted} max-w-2xl mb-8`}>
          An unforgettable 4-week cultural arts experience where children will move, create, drum, dance, sing, and shine!
        </p>
        
        <div className="flex flex-wrap gap-4 mb-8">
          <div className={`${theme.cardBg} rounded-xl px-6 py-4 border ${theme.cardBorder}`}>
            <p className={`${theme.textAccent} text-sm font-medium`}>Dates</p>
            <p className={`${theme.text} font-semibold`}>June 23 - July 16, 2026</p>
          </div>
          <div className={`${theme.cardBg} rounded-xl px-6 py-4 border ${theme.cardBorder}`}>
            <p className={`${theme.textAccent} text-sm font-medium`}>Duration</p>
            <p className={`${theme.text} font-semibold`}>4 Weeks</p>
          </div>
          <div className={`${theme.cardBg} rounded-xl px-6 py-4 border ${theme.cardBorder}`}>
            <p className={`${theme.textAccent} text-sm font-medium`}>Camp Days</p>
            <p className={`${theme.text} font-semibold`}>Tue, Wed & Thu</p>
          </div>
          <div className={`${theme.cardBg} rounded-xl px-6 py-4 border ${theme.cardBorder}`}>
            <p className={`${theme.textAccent} text-sm font-medium`}>Ages</p>
            <p className={`${theme.text} font-semibold`}>5-18 years</p>
          </div>
          <div className={`${theme.cardBg} rounded-xl px-6 py-4 border ${theme.cardBorder}`} data-testid="camp-location-chip">
            <p className={`${theme.textAccent} text-sm font-medium`}>Location</p>
            <p className={`${theme.text} font-semibold`}>Armature Works — 1910 N Ola Ave, Tampa, FL 33602</p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mb-8">
          <div className={`bg-gradient-to-br ${theme.primary} rounded-xl p-6 text-center`}>
            <p className="text-stone-800 text-sm font-medium mb-1">Early Bird Price</p>
            <p className="text-4xl font-bold text-stone-900">$125<span className="text-lg">/week</span></p>
            <p className="text-stone-800 text-sm mt-2">Balance due by May 1, 2026</p>
          </div>
          <div className={`${theme.cardBg} rounded-xl p-6 text-center border ${theme.cardBorder}`}>
            <p className={`${theme.textMuted} text-sm font-medium mb-1`}>Regular Price</p>
            <p className={`text-4xl font-bold ${theme.text}`}>$150<span className="text-lg">/week</span></p>
            <p className={`${theme.textMuted} text-sm mt-2`}>After May 1, 2026</p>
          </div>
        </div>

        {/* Showcase Info */}
        <div className={`${theme.cardBg} rounded-xl p-6 border ${theme.cardBorder} max-w-2xl`}>
          <div className="flex items-center gap-3">
            <Star className={`w-8 h-8 ${theme.textAccent}`} />
            <div>
              <p className={`${theme.text} font-semibold`}>Grand Finale Showcase</p>
              <p className={`${theme.textMuted}`}>July 17, 2026 - Campers perform everything they've learned!</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* About Camp */}
    <section className="py-16 bg-stone-900">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className={`font-display text-3xl font-bold ${theme.text} mb-8 text-center`}>
          Where Creativity Meets Culture
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className={`${theme.cardBg} rounded-2xl p-8 border ${theme.cardBorder}`}>
            <p className={`${theme.textMuted} leading-relaxed`}>
              Our 4-week immersive summer camp (Tuesday, Wednesday & Thursday) invites youth to explore the performing arts through drumming, dance, storytelling, song, and hands-on arts & crafts — all while building confidence, creativity, and cultural pride.
            </p>
            <p className={`${theme.textMuted} leading-relaxed mt-4`}>
              Throughout the program, guest artists will join us to inspire, teach, and expose campers to new artistic perspectives.
            </p>
          </div>
          
          <div className={`bg-gradient-to-br ${theme.primary} rounded-2xl p-8`}>
            <h3 className="font-display text-xl font-bold text-stone-900 mb-4">The Grand Finale</h3>
            <p className="text-stone-800">
              The entire camp experience culminates in a live theatrical performance on <strong>July 17, 2026</strong>, where campers take the stage to showcase everything they've learned!
            </p>
          </div>
        </div>

        {/* Activities */}
        <h3 className={`font-display text-2xl font-bold ${theme.text} mb-6 text-center`}>Camp Activities</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {[
            { icon: Drum, title: "Drumming", desc: "Traditional & contemporary rhythms" },
            { icon: Users, title: "Dance & Movement", desc: "Diverse cultural traditions" },
            { icon: Star, title: "Storytelling", desc: "Character expression & narrative" },
            { icon: Sparkles, title: "Arts & Crafts", desc: "Cultural visual art creation" },
            { icon: Music, title: "Theater", desc: "Choreography & performance" },
            { icon: Heart, title: "Community", desc: "Teamwork & cultural pride" }
          ].map((item, i) => (
            <div key={i} className={`${theme.cardBg} rounded-xl p-6 border ${theme.cardBorder} hover:border-opacity-100 transition-colors`}>
              <item.icon className={`w-8 h-8 ${theme.textAccent} mb-3`} />
              <h4 className={`font-semibold ${theme.text} mb-1`}>{item.title}</h4>
              <p className={`${theme.textMuted} text-sm`}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Gallery */}
        <h3 className={`font-display text-2xl font-bold ${theme.text} mb-6 text-center`}>See Our Campers in Action</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
          {ALL_IMAGES.slice(0, 8).map((img, i) => (
            <div key={i} className="aspect-square rounded-xl overflow-hidden shadow-lg">
              <img src={img} alt={`Camp activity ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>

        {/* Save the Date */}
        <div className="max-w-2xl mx-auto">
          <Card className={`${theme.cardBg} border ${theme.cardBorder}`} data-testid="camp-save-the-date-card">
            <CardHeader className="text-center">
              <CardTitle className={`font-display text-2xl ${theme.text}`}>Registration Is Closed for This Year</CardTitle>
              <CardDescription className={theme.textMuted}>
                Our Summer 2026 camp has wrapped — thank you to all our amazing campers and families!
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className={`bg-gradient-to-br ${theme.primary} rounded-xl p-6 mb-6`}>
                <Star className="w-8 h-8 text-stone-900 mx-auto mb-2" />
                <p className="text-stone-900 font-display text-xl font-bold">Save the Date: Summer Camp 2027</p>
                <p className="text-stone-800 mt-1">Dates and registration details will be announced soon!</p>
              </div>
              <p className={`${theme.textMuted} mb-4`}>
                Want to be the first to know when registration opens? Join the list and we'll email you the moment it does.
              </p>
              <CampNotifyForm />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  </div>
  );
};

// Theater Program Page
const TheaterProgramPage = () => (
  <div className="min-h-screen pt-20" data-testid="theater-program-page">
    <section className="py-24 bg-gradient-to-br from-stone-900 to-amber-950/30">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-amber-400 font-semibold tracking-wider text-sm uppercase">Year-Long Program</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 mt-2 mb-4">
            Youth & Community Theater Program
          </h1>
          <p className="text-amber-200/60 max-w-2xl mx-auto">
            An audition-based program where children and adults create culturally rooted theatrical storytelling productions
          </p>
        </div>

        <div className="bg-stone-800/50 rounded-3xl p-8 lg:p-12 border border-amber-900/30 mb-12">
          <p className="text-lg text-amber-100/90 leading-relaxed mb-6">
            Cultural Arts Theater offers a year-long youth and community theater program where children and adults audition and participate in culturally rooted theatrical storytelling productions.
          </p>

          <h3 className="font-display text-xl font-bold text-amber-200 mb-4">This program integrates:</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              "African drumming and dance",
              "Storytelling and theater arts",
              "Movement training and performance technique",
              "Leadership, confidence-building, and collaboration"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-amber-100/80">
                <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>

          <p className="text-amber-200/80 leading-relaxed mb-8">
            Participants rehearse throughout the year and take part in multiple performances and showcases. The program is designed to support artistic growth, cultural education, and positive youth development, while remaining accessible through scholarships, grants, and community partnerships.
          </p>

          <div className="bg-amber-900/30 rounded-xl p-6">
            <h4 className="font-semibold text-amber-200 mb-2">Interested in joining?</h4>
            <p className="text-amber-200/70 text-sm mb-4">Contact us to learn about upcoming auditions and enrollment.</p>
            <Link to="/contact">
              <Button className="bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full" data-testid="theater-contact-btn">
                Contact Us <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Gallery Page
const GalleryPage = () => {
  const { theme } = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  
  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      <section className="py-24 bg-gradient-to-br from-stone-900 to-stone-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className={`${theme.textAccent} font-semibold tracking-wider text-sm uppercase`}>Our Work</span>
            <h1 className={`font-display text-4xl sm:text-5xl lg:text-6xl font-bold ${theme.text} mt-2 mb-4`}>
              Gallery
            </h1>
            <p className={`${theme.textMuted} max-w-2xl mx-auto`}>
              Capturing the joy, energy, and cultural pride of our performances, classes, and community events
            </p>
          </div>

          {/* Video Section */}
          <div className="mb-16">
            <h2 className={`font-display text-2xl font-bold ${theme.text} mb-6`}>Featured Videos</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {ALL_VIDEOS.map((video, i) => (
                <div key={i} className="rounded-2xl overflow-hidden shadow-2xl">
                  <video
                    controls
                    poster={NEW_IMAGES[i] || CAMP_IMAGES[0]}
                    className="w-full aspect-video object-cover"
                  >
                    <source src={video.url} type="video/quicktime" />
                    <source src={video.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className={`${theme.cardBg} p-4`}>
                    <h3 className={`font-semibold ${theme.text}`}>{video.title}</h3>
                    <p className={`text-sm ${theme.textMuted}`}>{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Photo Gallery */}
          <div>
            <h2 className={`font-display text-2xl font-bold ${theme.text} mb-6`}>Photo Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {ALL_IMAGES.map((img, i) => (
                <div 
                  key={i} 
                  className="aspect-square rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                  onClick={() => setSelectedImage(img)}
                >
                  <img 
                    src={img} 
                    alt={`Gallery image ${i + 1}`} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-amber-400 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={selectedImage} 
            alt="Full size" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// Book Event Page
const BookEventPage = () => (
  <div className="min-h-screen pt-20" data-testid="book-event-page">
    <section className="py-24 bg-gradient-to-br from-stone-900 to-amber-950/30">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-amber-400 font-semibold tracking-wider text-sm uppercase">Performances</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 mt-2 mb-4">
            Book Us For Your Event
          </h1>
          <p className="text-amber-200/60 max-w-2xl mx-auto">
            Bring the energy and spirit of African drum and dance to your celebration!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Info Section */}
          <div className="space-y-8">
            <div>
              <h3 className="font-display text-xl font-bold text-amber-100 mb-4">We Perform At</h3>
              <div className="flex flex-wrap gap-2">
                {["Schools", "Birthday Parties", "Weddings", "Corporate Events", "Festivals", "Community Events", "Family Reunions"].map((venue, i) => (
                  <span key={i} className="bg-stone-800/50 border border-amber-900/30 text-amber-200 px-4 py-2 rounded-full text-sm">
                    {venue}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-display text-xl font-bold text-amber-100 mb-4">Performance Options</h3>
              <div className="space-y-4">
                {[
                  { title: "Drum & Dance Ensemble", desc: "High-energy African drum and dance performances" },
                  { title: "Interactive Performances", desc: "Engage your audience with participation activities" },
                  { title: "Theatrical Storytelling", desc: "Featuring acting, drumming, dance, and narration" }
                ].map((item, i) => (
                  <div key={i} className="bg-stone-800/50 rounded-xl p-4 border border-amber-900/30">
                    <h4 className="font-semibold text-amber-200 mb-1">{item.title}</h4>
                    <p className="text-amber-200/60 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-display text-xl font-bold text-amber-100 mb-4">Our Performers</h3>
              <p className="text-amber-200/80">
                Choose from youth performers, adult performers, or a combination based on your event needs and preferences.
              </p>
            </div>
          </div>

          {/* Form Section */}
          <Card className="bg-stone-800/50 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-display text-2xl text-amber-100">Request a Quote</CardTitle>
              <CardDescription className="text-amber-200/60">Tell us about your event</CardDescription>
            </CardHeader>
            <CardContent>
              <EventBookingForm />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  </div>
);

// Donate Page
const DonatePage = () => {
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const presetAmounts = ["25", "50", "100", "250", "500"];

  const handleDonate = async () => {
    const donationAmount = amount === "custom" ? parseFloat(customAmount) : parseFloat(amount);
    
    if (!donationAmount || donationAmount < 1) {
      toast.error("Please enter a valid donation amount (minimum $1)");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/donate/checkout`, {
        amount: donationAmount,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to process donation. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="donate-page">
      <section className="py-24 bg-gradient-to-br from-stone-900 to-red-950/30">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <Heart className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 mb-4">
              Support the Arts
            </h1>
            <p className="text-amber-200/60 max-w-2xl mx-auto">
              Your generous donation directly supports arts education for youth and families in our community
            </p>
          </div>

          <Card className="bg-stone-800/50 border-amber-900/30 mb-12">
            <CardContent className="p-8">
              <h3 className="font-display text-xl font-bold text-amber-100 mb-6">Your donation supports:</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  "Summer camp scholarships",
                  "Year-round youth programs",
                  "Community classes",
                  "Access for underserved families"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-amber-100/80">
                    <CheckCircle2 className="w-5 h-5 text-red-400 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-amber-200 mb-3 block">Select Amount</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {presetAmounts.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setAmount(preset)}
                        data-testid={`amount-${preset}`}
                        className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                          amount === preset
                            ? "bg-red-500 text-white"
                            : "bg-stone-700/50 text-amber-200 hover:bg-stone-700"
                        }`}
                      >
                        ${preset}
                      </button>
                    ))}
                    <button
                      onClick={() => setAmount("custom")}
                      data-testid="amount-custom"
                      className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                        amount === "custom"
                          ? "bg-red-500 text-white"
                          : "bg-stone-700/50 text-amber-200 hover:bg-stone-700"
                      }`}
                    >
                      Other
                    </button>
                  </div>
                </div>

                {amount === "custom" && (
                  <div>
                    <Label htmlFor="customAmount" className="text-amber-200">Custom Amount ($)</Label>
                    <Input
                      id="customAmount"
                      type="number"
                      min="1"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-2"
                      data-testid="custom-amount-input"
                    />
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="donorName" className="text-amber-200">Name (Optional)</Label>
                    <Input
                      id="donorName"
                      placeholder="Your name"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-2"
                      data-testid="donor-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="donorEmail" className="text-amber-200">Email (Optional)</Label>
                    <Input
                      id="donorEmail"
                      type="email"
                      placeholder="your@email.com"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-2"
                      data-testid="donor-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-amber-200">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    placeholder="Leave a message with your donation"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-2"
                    data-testid="donor-message-input"
                  />
                </div>

                <Button
                  onClick={handleDonate}
                  disabled={loading || !amount}
                  className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-6 text-lg rounded-xl"
                  data-testid="donate-submit-btn"
                >
                  {loading ? "Processing..." : (
                    <>
                      <Heart className="mr-2" /> Donate {amount && amount !== "custom" ? `$${amount}` : (customAmount ? `$${customAmount}` : "")}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

// Donation Success Page
const DonateSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [pollCount, setPollCount] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/donate/status/${sessionId}`);
        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("expired");
        } else if (pollCount < 5) {
          setPollCount(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error(error);
        setStatus("error");
      }
    };

    checkStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="donate-success-page">
      <div className="max-w-md mx-auto px-4 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-display text-2xl font-bold text-amber-100 mb-2">Processing Your Donation</h1>
            <p className="text-amber-200/60">Please wait while we confirm your payment...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <CheckCircle2 className="w-20 h-20 text-green-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold text-amber-100 mb-2">Thank You!</h1>
            <p className="text-amber-200/80 mb-8">
              Your generous donation helps us continue providing arts education to youth and families in our community.
            </p>
            <Link to="/">
              <Button className="bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full px-8">
                Return Home
              </Button>
            </Link>
          </>
        )}

        {(status === "error" || status === "expired") && (
          <>
            <X className="w-20 h-20 text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold text-amber-100 mb-2">Something Went Wrong</h1>
            <p className="text-amber-200/80 mb-8">
              We couldn't confirm your donation. Please try again or contact us for assistance.
            </p>
            <Link to="/donate">
              <Button className="bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full px-8">
                Try Again
              </Button>
            </Link>
          </>
        )}

        {status === "pending" && (
          <>
            <Clock className="w-20 h-20 text-amber-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold text-amber-100 mb-2">Payment Pending</h1>
            <p className="text-amber-200/80 mb-8">
              Your payment is still being processed. You should receive a confirmation email soon.
            </p>
            <Link to="/">
              <Button className="bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full px-8">
                Return Home
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const { theme } = useTheme();
  
  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
    <section className="py-24 bg-gradient-to-br from-stone-900 to-amber-950/30">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className={`${theme.textAccent} font-semibold tracking-wider text-sm uppercase`}>Get In Touch</span>
          <h1 className={`font-display text-4xl sm:text-5xl lg:text-6xl font-bold ${theme.text} mt-2 mb-4`}>
            Contact Us
          </h1>
          <p className={`${theme.textMuted} max-w-2xl mx-auto`}>
            Have questions? We'd love to hear from you!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${theme.highlight} flex items-center justify-center flex-shrink-0`}>
                    <MapPin className={`w-6 h-6 ${theme.textAccent}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${theme.text} mb-1`}>Location</h3>
                    <p className={`${theme.textMuted}`}>Tampa, Florida</p>
                    <p className={`${theme.textMuted} text-sm mt-1`}>Serving the Tampa Bay community</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${theme.highlight} flex items-center justify-center flex-shrink-0`}>
                    <Mail className={`w-6 h-6 ${theme.textAccent}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${theme.text} mb-1`}>Email</h3>
                    <a href="mailto:info@culturalartstheater.org" className={`${theme.textAccent} hover:underline`}>
                      info@culturalartstheater.org
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${theme.highlight} flex items-center justify-center flex-shrink-0`}>
                    <Phone className={`w-6 h-6 ${theme.textAccent}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${theme.text} mb-1`}>Phone</h3>
                    <a href="tel:813-395-3402" className={`${theme.textAccent} hover:underline`}>
                      (813) 395-3402
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${theme.highlight} flex items-center justify-center flex-shrink-0`}>
                    <Calendar className={`w-6 h-6 ${theme.textAccent}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${theme.text} mb-1`}>Weekly Community Classes</h3>
                    <p className={`${theme.textMuted}`}>Every Tuesday</p>
                    <p className={`${theme.textMuted} text-sm`}>Drum: 6:30 PM | Dance: 7:30 PM</p>
                    <p className={`${theme.textMuted} text-sm mt-1`}>Location: 2005 N Lamar Ave, Tampa, FL</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className={`bg-gradient-to-br ${theme.primary} rounded-2xl p-8`}>
              <h3 className="font-display text-xl font-bold text-stone-900 mb-4">Ready to Connect?</h3>
              <p className="text-stone-800 mb-6">
                Whether you want to register for a class, book a performance, or learn more about our programs, we're here to help!
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/classes">
                  <Button variant="secondary" className="bg-stone-900/20 text-stone-900 hover:bg-stone-900/30 rounded-full">
                    View Classes
                  </Button>
                </Link>
                <Link to="/book-event">
                  <Button variant="secondary" className="bg-stone-900/20 text-stone-900 hover:bg-stone-900/30 rounded-full">
                    Book Performance
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
            <CardHeader>
              <CardTitle className={`font-display text-2xl ${theme.text}`}>Send a Message</CardTitle>
              <CardDescription className={theme.textMuted}>We'll get back to you as soon as possible</CardDescription>
            </CardHeader>
            <CardContent>
              <ContactForm />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  </div>
  );
};

// ============== FORM COMPONENTS ==============

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", subject: "", message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-amber-200">Name *</Label>
          <Input
            id="name"
            required
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="contact-name"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-amber-200">Email *</Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="contact-email"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone" className="text-amber-200">Phone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="contact-phone"
          />
        </div>
        <div>
          <Label htmlFor="subject" className="text-amber-200">Subject *</Label>
          <Input
            id="subject"
            required
            value={formData.subject}
            onChange={(e) => setFormData({...formData, subject: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="contact-subject"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="message" className="text-amber-200">Message *</Label>
        <Textarea
          id="message"
          required
          rows={5}
          value={formData.message}
          onChange={(e) => setFormData({...formData, message: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="contact-message"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full py-6"
        data-testid="contact-submit"
      >
        {loading ? "Sending..." : "Send Message"}
      </Button>
    </form>
  );
};

const CommunityClassForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", num_adults: 1, num_children: 0, message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/classes/community/register`, formData);
      toast.success("Registration submitted! We'll see you in class!");
      setFormData({ name: "", email: "", phone: "", num_adults: 1, num_children: 0, message: "" });
    } catch (error) {
      toast.error("Failed to submit registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white/10 rounded-xl p-6 space-y-4" data-testid="community-class-form">
      <h4 className="text-white font-semibold text-lg">Quick Registration</h4>
      <div>
        <Label htmlFor="cc-name" className="text-amber-100">Name *</Label>
        <Input
          id="cc-name"
          required
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="bg-white/20 border-0 text-white placeholder:text-amber-200/50 mt-1"
          placeholder="Your name"
          data-testid="cc-name"
        />
      </div>
      <div>
        <Label htmlFor="cc-email" className="text-amber-100">Email *</Label>
        <Input
          id="cc-email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          className="bg-white/20 border-0 text-white placeholder:text-amber-200/50 mt-1"
          placeholder="your@email.com"
          data-testid="cc-email"
        />
      </div>
      <div>
        <Label htmlFor="cc-phone" className="text-amber-100">Phone *</Label>
        <Input
          id="cc-phone"
          required
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
          className="bg-white/20 border-0 text-white placeholder:text-amber-200/50 mt-1"
          placeholder="(555) 123-4567"
          data-testid="cc-phone"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cc-adults" className="text-amber-100">Adults</Label>
          <Input
            id="cc-adults"
            type="number"
            min="0"
            value={formData.num_adults}
            onChange={(e) => setFormData({...formData, num_adults: parseInt(e.target.value) || 0})}
            className="bg-white/20 border-0 text-white mt-1"
            data-testid="cc-adults"
          />
        </div>
        <div>
          <Label htmlFor="cc-children" className="text-amber-100">Children</Label>
          <Input
            id="cc-children"
            type="number"
            min="0"
            value={formData.num_children}
            onChange={(e) => setFormData({...formData, num_children: parseInt(e.target.value) || 0})}
            className="bg-white/20 border-0 text-white mt-1"
            data-testid="cc-children"
          />
        </div>
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-white text-amber-700 hover:bg-amber-50 rounded-full"
        data-testid="cc-submit"
      >
        {loading ? "Submitting..." : "Register for Class"}
      </Button>
    </form>
  );
};

const HomeschoolForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: "", email: "", phone: "", num_students: 1, ages: "",
    address: "", hours_requested: 1, preferred_days: "", preferred_times: "",
    class_type: "", goals_notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/classes/homeschool/request`, formData);
      toast.success("Request submitted! We'll contact you soon to discuss details.");
      setFormData({
        contact_name: "", email: "", phone: "", num_students: 1, ages: "",
        address: "", hours_requested: 1, preferred_days: "", preferred_times: "",
        class_type: "", goals_notes: ""
      });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="homeschool-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="hs-name" className="text-amber-200">Contact Name *</Label>
          <Input
            id="hs-name"
            required
            value={formData.contact_name}
            onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-name"
          />
        </div>
        <div>
          <Label htmlFor="hs-email" className="text-amber-200">Email *</Label>
          <Input
            id="hs-email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-email"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="hs-phone" className="text-amber-200">Phone *</Label>
          <Input
            id="hs-phone"
            required
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-phone"
          />
        </div>
        <div>
          <Label htmlFor="hs-students" className="text-amber-200">Number of Students *</Label>
          <Input
            id="hs-students"
            type="number"
            min="1"
            required
            value={formData.num_students}
            onChange={(e) => setFormData({...formData, num_students: parseInt(e.target.value) || 1})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-students"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="hs-ages" className="text-amber-200">Ages of Students *</Label>
        <Input
          id="hs-ages"
          required
          placeholder="e.g., 6, 8, 10"
          value={formData.ages}
          onChange={(e) => setFormData({...formData, ages: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="hs-ages"
        />
      </div>
      <div>
        <Label htmlFor="hs-address" className="text-amber-200">Location/Address *</Label>
        <Input
          id="hs-address"
          required
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="hs-address"
        />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="hs-hours" className="text-amber-200">Hours Requested *</Label>
          <Input
            id="hs-hours"
            type="number"
            min="0.5"
            step="0.5"
            required
            value={formData.hours_requested}
            onChange={(e) => setFormData({...formData, hours_requested: parseFloat(e.target.value) || 1})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-hours"
          />
        </div>
        <div>
          <Label htmlFor="hs-days" className="text-amber-200">Preferred Days *</Label>
          <Input
            id="hs-days"
            required
            placeholder="e.g., Mon, Wed"
            value={formData.preferred_days}
            onChange={(e) => setFormData({...formData, preferred_days: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-days"
          />
        </div>
        <div>
          <Label htmlFor="hs-times" className="text-amber-200">Preferred Times *</Label>
          <Input
            id="hs-times"
            required
            placeholder="e.g., 10 AM"
            value={formData.preferred_times}
            onChange={(e) => setFormData({...formData, preferred_times: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="hs-times"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="hs-type" className="text-amber-200">Class Type *</Label>
        <Select value={formData.class_type} onValueChange={(v) => setFormData({...formData, class_type: v})} required>
          <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="hs-class-type">
            <SelectValue placeholder="Select class type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="drum">Drum Only</SelectItem>
            <SelectItem value="dance">Dance Only</SelectItem>
            <SelectItem value="both">Drum & Dance</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="hs-goals" className="text-amber-200">Goals or Additional Notes</Label>
        <Textarea
          id="hs-goals"
          value={formData.goals_notes}
          onChange={(e) => setFormData({...formData, goals_notes: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="hs-goals"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full py-6"
        data-testid="hs-submit"
      >
        {loading ? "Submitting..." : "Submit Request"}
      </Button>
    </form>
  );
};

const CorporateForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: "", organization: "", email: "", phone: "", num_participants: 10,
    class_length: "", class_type: "", event_type: "", preferred_date: "",
    preferred_time: "", location: "", notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/classes/corporate/request`, formData);
      toast.success("Request submitted! We'll contact you soon to discuss details.");
      setFormData({
        contact_name: "", organization: "", email: "", phone: "", num_participants: 10,
        class_length: "", class_type: "", event_type: "", preferred_date: "",
        preferred_time: "", location: "", notes: ""
      });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="corporate-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="corp-name" className="text-amber-200">Contact Name *</Label>
          <Input
            id="corp-name"
            required
            value={formData.contact_name}
            onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-name"
          />
        </div>
        <div>
          <Label htmlFor="corp-org" className="text-amber-200">Organization *</Label>
          <Input
            id="corp-org"
            required
            value={formData.organization}
            onChange={(e) => setFormData({...formData, organization: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-org"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="corp-email" className="text-amber-200">Email *</Label>
          <Input
            id="corp-email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-email"
          />
        </div>
        <div>
          <Label htmlFor="corp-phone" className="text-amber-200">Phone *</Label>
          <Input
            id="corp-phone"
            required
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-phone"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="corp-participants" className="text-amber-200">Participants *</Label>
          <Input
            id="corp-participants"
            type="number"
            min="1"
            required
            value={formData.num_participants}
            onChange={(e) => setFormData({...formData, num_participants: parseInt(e.target.value) || 10})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-participants"
          />
        </div>
        <div>
          <Label htmlFor="corp-length" className="text-amber-200">Class Length *</Label>
          <Select value={formData.class_length} onValueChange={(v) => setFormData({...formData, class_length: v})} required>
            <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="corp-length">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1hour">1 Hour</SelectItem>
              <SelectItem value="1.5hours">1.5 Hours</SelectItem>
              <SelectItem value="2hours">2 Hours</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="corp-type" className="text-amber-200">Class Type *</Label>
          <Select value={formData.class_type} onValueChange={(v) => setFormData({...formData, class_type: v})} required>
            <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="corp-type">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="drum">Drum</SelectItem>
              <SelectItem value="dance">Dance</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="corp-event" className="text-amber-200">Event Type *</Label>
        <Input
          id="corp-event"
          required
          placeholder="e.g., Team Building, Wellness Day"
          value={formData.event_type}
          onChange={(e) => setFormData({...formData, event_type: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="corp-event"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="corp-date" className="text-amber-200">Preferred Date *</Label>
          <Input
            id="corp-date"
            type="date"
            required
            value={formData.preferred_date}
            onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-date"
          />
        </div>
        <div>
          <Label htmlFor="corp-time" className="text-amber-200">Preferred Time *</Label>
          <Input
            id="corp-time"
            required
            placeholder="e.g., 2:00 PM"
            value={formData.preferred_time}
            onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="corp-time"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="corp-location" className="text-amber-200">Location *</Label>
        <Input
          id="corp-location"
          required
          value={formData.location}
          onChange={(e) => setFormData({...formData, location: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="corp-location"
        />
      </div>
      <div>
        <Label htmlFor="corp-notes" className="text-amber-200">Additional Notes</Label>
        <Textarea
          id="corp-notes"
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="corp-notes"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full py-6"
        data-testid="corp-submit"
      >
        {loading ? "Submitting..." : "Submit Request"}
      </Button>
    </form>
  );
};

const EventBookingForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: "", email: "", phone: "", event_type: "", event_date: "",
    event_time: "", location: "", num_performers: 3, performer_type: "",
    performance_length: "", performance_type: "", audience_participation: false,
    additional_notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/events/booking`, formData);
      toast.success("Quote request submitted! We'll be in touch soon with pricing details.");
      setFormData({
        contact_name: "", email: "", phone: "", event_type: "", event_date: "",
        event_time: "", location: "", num_performers: 3, performer_type: "",
        performance_length: "", performance_type: "", audience_participation: false,
        additional_notes: ""
      });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-booking-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="event-name" className="text-amber-200">Contact Name *</Label>
          <Input
            id="event-name"
            required
            value={formData.contact_name}
            onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="event-name"
          />
        </div>
        <div>
          <Label htmlFor="event-email" className="text-amber-200">Email *</Label>
          <Input
            id="event-email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="event-email"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="event-phone" className="text-amber-200">Phone *</Label>
          <Input
            id="event-phone"
            required
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="event-phone"
          />
        </div>
        <div>
          <Label htmlFor="event-type" className="text-amber-200">Event Type *</Label>
          <Select value={formData.event_type} onValueChange={(v) => setFormData({...formData, event_type: v})} required>
            <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="event-type">
              <SelectValue placeholder="Select event type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="school">School</SelectItem>
              <SelectItem value="birthday">Birthday Party</SelectItem>
              <SelectItem value="wedding">Wedding</SelectItem>
              <SelectItem value="corporate">Corporate Event</SelectItem>
              <SelectItem value="festival">Festival</SelectItem>
              <SelectItem value="community">Community Event</SelectItem>
              <SelectItem value="reunion">Family Reunion</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="event-date" className="text-amber-200">Event Date *</Label>
          <Input
            id="event-date"
            type="date"
            required
            value={formData.event_date}
            onChange={(e) => setFormData({...formData, event_date: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="event-date"
          />
        </div>
        <div>
          <Label htmlFor="event-time" className="text-amber-200">Event Time *</Label>
          <Input
            id="event-time"
            required
            placeholder="e.g., 3:00 PM"
            value={formData.event_time}
            onChange={(e) => setFormData({...formData, event_time: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="event-time"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="event-location" className="text-amber-200">Event Location *</Label>
        <Input
          id="event-location"
          required
          value={formData.location}
          onChange={(e) => setFormData({...formData, location: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="event-location"
        />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="event-performers" className="text-amber-200">Performers *</Label>
          <Input
            id="event-performers"
            type="number"
            min="1"
            required
            value={formData.num_performers}
            onChange={(e) => setFormData({...formData, num_performers: parseInt(e.target.value) || 3})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="event-performers"
          />
        </div>
        <div>
          <Label htmlFor="event-performer-type" className="text-amber-200">Performer Type *</Label>
          <Select value={formData.performer_type} onValueChange={(v) => setFormData({...formData, performer_type: v})} required>
            <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="event-performer-type">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="youth">Youth</SelectItem>
              <SelectItem value="adult">Adult</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="event-length" className="text-amber-200">Length *</Label>
          <Select value={formData.performance_length} onValueChange={(v) => setFormData({...formData, performance_length: v})} required>
            <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="event-length">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15min">15 minutes</SelectItem>
              <SelectItem value="30min">30 minutes</SelectItem>
              <SelectItem value="45min">45 minutes</SelectItem>
              <SelectItem value="1hour">1 hour</SelectItem>
              <SelectItem value="1.5hours">1.5 hours</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="event-performance-type" className="text-amber-200">Performance Type *</Label>
        <Select value={formData.performance_type} onValueChange={(v) => setFormData({...formData, performance_type: v})} required>
          <SelectTrigger className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1" data-testid="event-performance-type">
            <SelectValue placeholder="Select performance type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="drum_dance">Drum & Dance Ensemble</SelectItem>
            <SelectItem value="theatrical">Theatrical Storytelling</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3">
        <Checkbox
          id="event-participation"
          checked={formData.audience_participation}
          onCheckedChange={(checked) => setFormData({...formData, audience_participation: !!checked})}
          data-testid="event-participation"
        />
        <Label htmlFor="event-participation" className="text-amber-200 cursor-pointer">
          Include audience participation
        </Label>
      </div>
      <div>
        <Label htmlFor="event-notes" className="text-amber-200">Additional Notes</Label>
        <Textarea
          id="event-notes"
          value={formData.additional_notes}
          onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          placeholder="Any special requests or requirements?"
          data-testid="event-notes"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full py-6"
        data-testid="event-submit"
      >
        {loading ? "Submitting..." : "Request Quote"}
      </Button>
    </form>
  );
};

const CampRegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    parent_name: "", email: "", phone: "", child_name: "", child_age: "",
    emergency_contact: "", emergency_phone: "", medical_notes: "", how_heard: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/camp/register`, {
        ...formData,
        child_age: parseInt(formData.child_age)
      });
      toast.success("Registration submitted! We'll be in touch with next steps.");
      setFormData({
        parent_name: "", email: "", phone: "", child_name: "", child_age: "",
        emergency_contact: "", emergency_phone: "", medical_notes: "", how_heard: ""
      });
    } catch (error) {
      toast.error("Failed to submit registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="camp-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="camp-parent" className="text-amber-200">Parent/Guardian Name *</Label>
          <Input
            id="camp-parent"
            required
            value={formData.parent_name}
            onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="camp-parent"
          />
        </div>
        <div>
          <Label htmlFor="camp-email" className="text-amber-200">Email *</Label>
          <Input
            id="camp-email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="camp-email"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="camp-phone" className="text-amber-200">Phone *</Label>
          <Input
            id="camp-phone"
            required
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="camp-phone"
          />
        </div>
        <div>
          <Label htmlFor="camp-child" className="text-amber-200">Child's Name *</Label>
          <Input
            id="camp-child"
            required
            value={formData.child_name}
            onChange={(e) => setFormData({...formData, child_name: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="camp-child"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="camp-age" className="text-amber-200">Child's Age *</Label>
        <Input
          id="camp-age"
          type="number"
          min="5"
          max="18"
          required
          value={formData.child_age}
          onChange={(e) => setFormData({...formData, child_age: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="camp-age"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="camp-emergency" className="text-amber-200">Emergency Contact *</Label>
          <Input
            id="camp-emergency"
            required
            value={formData.emergency_contact}
            onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="camp-emergency"
          />
        </div>
        <div>
          <Label htmlFor="camp-emergency-phone" className="text-amber-200">Emergency Phone *</Label>
          <Input
            id="camp-emergency-phone"
            required
            value={formData.emergency_phone}
            onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
            className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
            data-testid="camp-emergency-phone"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="camp-medical" className="text-amber-200">Medical Notes/Allergies</Label>
        <Textarea
          id="camp-medical"
          value={formData.medical_notes}
          onChange={(e) => setFormData({...formData, medical_notes: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          placeholder="Any medical conditions or allergies we should know about?"
          data-testid="camp-medical"
        />
      </div>
      <div>
        <Label htmlFor="camp-heard" className="text-amber-200">How did you hear about us?</Label>
        <Input
          id="camp-heard"
          value={formData.how_heard}
          onChange={(e) => setFormData({...formData, how_heard: e.target.value})}
          className="bg-stone-700/50 border-amber-900/30 text-amber-100 mt-1"
          data-testid="camp-heard"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-full py-6"
        data-testid="camp-submit"
      >
        {loading ? "Submitting..." : "Register for Summer Camp"}
      </Button>
    </form>
  );
};

// ============== ADMIN DASHBOARD ==============

const AdminDashboard = () => {
  const { theme } = useTheme();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("camp");
  const [data, setData] = useState({ registrations: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [blastSubject, setBlastSubject] = useState("Summer Camp 2027 Registration Is Open!");
  const [blastMessage, setBlastMessage] = useState("Great news! Registration for Summer Camp 2027 is officially open.\nSpots fill up fast, so head to our website today to secure your camper's place for an unforgettable summer of drumming, dancing, storytelling, and cultural arts.\nWe can't wait to see you this summer!");
  const [sendingBlast, setSendingBlast] = useState(false);
  const [sendingClassReminders, setSendingClassReminders] = useState(false);
  const [wesleySubject, setWesleySubject] = useState("Wesley Chapel Classes Are Launching!");
  const [wesleyMessage, setWesleyMessage] = useState("Great news! Our Wesley Chapel drum & dance classes are officially launching.\nLocation: [add location here]\nFirst class: [add date and time here]\nSpots are limited, so reply to this email or call us to confirm your place. We can't wait to drum and dance with you!");
  const [sendingWesleyBlast, setSendingWesleyBlast] = useState(false);

  const sendWesleyBlast = async () => {
    if (!window.confirm(`Send this email to all ${data.count || 0} Wesley Chapel pre-registrants?`)) return;
    setSendingWesleyBlast(true);
    try {
      const res = await axios.post(`${API}/admin/wesley-blast?password=${password}`, {
        subject: wesleySubject,
        message: wesleyMessage
      });
      toast.success(`Blast sent to ${res.data.sent} of ${res.data.total} pre-registrants!`);
    } catch (error) {
      toast.error("Failed to send blast");
    } finally {
      setSendingWesleyBlast(false);
    }
  };
  const [checkinDate, setCheckinDate] = useState(() => {
    const today = new Date();
    if (today < new Date("2026-08-25T00:00:00")) return "2026-08-25";
    const d = new Date(today);
    d.setDate(d.getDate() - ((d.getDay() - 2 + 7) % 7));
    return d.toISOString().slice(0, 10);
  });
  const [attendance, setAttendance] = useState([]);
  const [attendanceCounts, setAttendanceCounts] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const [walkinName, setWalkinName] = useState("");
  const [walkinPhone, setWalkinPhone] = useState("");
  const [addingWalkin, setAddingWalkin] = useState(false);

  const addWalkin = async (e) => {
    e.preventDefault();
    if (!walkinName.trim()) return;
    setAddingWalkin(true);
    try {
      await axios.post(`${API}/admin/walkin?password=${password}`, {
        name: walkinName.trim(),
        phone: walkinPhone.trim() || null,
        class_date: checkinDate
      });
      toast.success(`${walkinName.trim()} added and checked in!`);
      setWalkinName("");
      setWalkinPhone("");
      fetchData("checkin");
      fetchAttendance(checkinDate);
    } catch (error) {
      toast.error("Failed to add walk-in");
    } finally {
      setAddingWalkin(false);
    }
  };

  const fetchAttendance = async (date) => {
    try {
      const [attRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/attendance?password=${password}&class_date=${date}`),
        axios.get(`${API}/admin/attendance/summary?password=${password}`)
      ]);
      setAttendance(attRes.data.registrant_ids || []);
      setAttendanceCounts(sumRes.data.counts || {});
    } catch (error) {
      toast.error("Failed to load attendance");
    }
  };

  const handleCheckinDateChange = (date) => {
    setCheckinDate(date);
    fetchAttendance(date);
  };

  const toggleCheckin = async (reg) => {
    setTogglingId(reg.id);
    try {
      const res = await axios.post(`${API}/admin/attendance/toggle?password=${password}`, {
        class_date: checkinDate,
        registrant_id: reg.id,
        name: reg.name,
        email: reg.email
      });
      setAttendance((prev) => res.data.checked_in ? [...prev, reg.id] : prev.filter((id) => id !== reg.id));
      setAttendanceCounts((prev) => ({ ...prev, [reg.id]: Math.max(0, (prev[reg.id] || 0) + (res.data.checked_in ? 1 : -1)) }));
    } catch (error) {
      toast.error("Failed to update check-in");
    } finally {
      setTogglingId(null);
    }
  };

  const sendBlast = async () => {
    if (!window.confirm(`Send this email blast to all ${data.count || 0} families on the Camp 2027 interest list?`)) return;
    setSendingBlast(true);
    try {
      const res = await axios.post(`${API}/admin/camp-notify-blast?password=${password}`, {
        subject: blastSubject,
        message: blastMessage
      });
      toast.success(`Blast sent to ${res.data.sent} of ${res.data.total} families!`);
    } catch (error) {
      toast.error("Failed to send blast");
    } finally {
      setSendingBlast(false);
    }
  };

  const sendClassReminders = async () => {
    if (!window.confirm("Send the Tuesday class reminder email to ALL community class registrants right now?")) return;
    setSendingClassReminders(true);
    try {
      const res = await axios.post(`${API}/admin/send-class-reminders?password=${password}`);
      toast.success(`Reminders sent to ${res.data.sent} registrants!`);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingClassReminders(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsLoggedIn(true);
        setLoginError("");
        fetchData("camp");
      }
    } catch (error) {
      setLoginError("Invalid password");
    }
  };

  const fetchData = async (tab) => {
    setLoading(true);
    try {
      let endpoint = "";
      switch (tab) {
        case "camp": endpoint = "/admin/camp-registrations"; break;
        case "notify": endpoint = "/admin/camp-notify-list"; break;
        case "checkin": endpoint = "/admin/community-registrations"; break;
        case "wesley": endpoint = "/admin/wesley-preregistrations"; break;
        case "community": endpoint = "/admin/community-registrations"; break;
        case "events": endpoint = "/admin/event-bookings"; break;
        case "contact": endpoint = "/admin/contact-submissions"; break;
        case "homeschool": endpoint = "/admin/homeschool-requests"; break;
        case "corporate": endpoint = "/admin/corporate-requests"; break;
        default: endpoint = "/admin/camp-registrations";
      }
      const response = await axios.get(`${API}${endpoint}?password=${password}`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    fetchData(tab);
    if (tab === "checkin") fetchAttendance(checkinDate);
  };

  const sendReminder = async (registration) => {
    setSendingReminder(registration.id);
    try {
      await axios.post(`${API}/admin/send-payment-reminder?password=${password}`, {
        registration_id: registration.id,
        email: registration.email,
        child_name: registration.child_name,
        parent_name: registration.parent_name
      });
      toast.success(`Reminder sent to ${registration.email}`);
    } catch (error) {
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="admin-login">
        <Card className={`${theme.cardBg} border ${theme.cardBorder} w-full max-w-md`}>
          <CardHeader className="text-center">
            <Lock className={`w-12 h-12 ${theme.textAccent} mx-auto mb-4`} />
            <CardTitle className={`font-display text-2xl ${theme.text}`}>Admin Dashboard</CardTitle>
            <CardDescription className={theme.textMuted}>Enter password to access</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-stone-700/50 border-amber-900/30 text-amber-100"
                  data-testid="admin-password"
                />
              </div>
              {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
              <Button type="submit" className={`w-full ${theme.buttonPrimary} rounded-full`} data-testid="admin-login-btn">
                <Lock className="w-4 h-4 mr-2" /> Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: "camp", label: "Camp Registrations", icon: Calendar },
    { id: "notify", label: "Camp 2027 Interest", icon: Bell },
    { id: "community", label: "Community Classes", icon: Users },
    { id: "checkin", label: "Class Check-In", icon: CheckCircle2 },
    { id: "wesley", label: "Wesley Chapel", icon: MapPin },
    { id: "events", label: "Event Bookings", icon: Music },
    { id: "homeschool", label: "Homeschool", icon: Star },
    { id: "corporate", label: "Corporate", icon: Sparkles },
    { id: "contact", label: "Contact Forms", icon: Mail }
  ];

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className={`font-display text-3xl font-bold ${theme.text}`}>Admin Dashboard</h1>
          <Button 
            onClick={() => fetchData(activeTab)} 
            variant="outline" 
            className={`${theme.buttonSecondary} rounded-full`}
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${theme.primary} text-stone-900`
                  : `${theme.cardBg} ${theme.textMuted} hover:${theme.text}`
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats Card */}
        <Card className={`${theme.cardBg} border ${theme.cardBorder} mb-6`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${theme.primary} flex items-center justify-center`}>
                <span className="text-2xl font-bold text-stone-900">{data.count || data.registrations?.length || data.bookings?.length || data.submissions?.length || data.requests?.length || 0}</span>
              </div>
              <div>
                <p className={`${theme.text} font-semibold text-lg`}>Total {tabs.find(t => t.id === activeTab)?.label}</p>
                <p className={theme.textMuted}>All time submissions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Camp 2027 Blast */}
        {activeTab === "notify" && (
          <Card className={`${theme.cardBg} border ${theme.cardBorder} mb-6`} data-testid="camp-blast-card">
            <CardHeader>
              <CardTitle className={`font-display text-xl ${theme.text}`}>Email Blast — Registration Announcement</CardTitle>
              <CardDescription className={theme.textMuted}>One click sends this email to everyone on the interest list below.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                value={blastSubject}
                onChange={(e) => setBlastSubject(e.target.value)}
                placeholder="Email subject"
                className="bg-stone-700/50 border-amber-900/30 text-amber-100"
                data-testid="blast-subject"
              />
              <Textarea
                value={blastMessage}
                onChange={(e) => setBlastMessage(e.target.value)}
                rows={4}
                placeholder="Email message"
                className="bg-stone-700/50 border-amber-900/30 text-amber-100"
                data-testid="blast-message"
              />
              <Button
                onClick={sendBlast}
                disabled={sendingBlast || !blastSubject || !blastMessage || (data.count || 0) === 0}
                className={`${theme.buttonPrimary} rounded-full px-6`}
                data-testid="blast-send-btn"
              >
                {sendingBlast ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Blast to {data.count || 0} Families
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Wesley Chapel Blast */}
        {activeTab === "wesley" && (
          <Card className={`${theme.cardBg} border ${theme.cardBorder} mb-6`} data-testid="wesley-blast-card">
            <CardHeader>
              <CardTitle className={`font-display text-xl ${theme.text}`}>Email Blast — Launch Announcement</CardTitle>
              <CardDescription className={theme.textMuted}>Fill in the location and start date, then one click emails everyone on the pre-registration list below.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                value={wesleySubject}
                onChange={(e) => setWesleySubject(e.target.value)}
                placeholder="Email subject"
                className="bg-stone-700/50 border-amber-900/30 text-amber-100"
                data-testid="wesley-blast-subject"
              />
              <Textarea
                value={wesleyMessage}
                onChange={(e) => setWesleyMessage(e.target.value)}
                rows={5}
                placeholder="Email message"
                className="bg-stone-700/50 border-amber-900/30 text-amber-100"
                data-testid="wesley-blast-message"
              />
              <Button
                onClick={sendWesleyBlast}
                disabled={sendingWesleyBlast || !wesleySubject || !wesleyMessage || (data.count || 0) === 0}
                className={`${theme.buttonPrimary} rounded-full px-6`}
                data-testid="wesley-blast-send-btn"
              >
                {sendingWesleyBlast ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Blast to {data.count || 0} Pre-Registrants
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Class Reminders */}
        {activeTab === "community" && (
          <Card className={`${theme.cardBg} border ${theme.cardBorder} mb-6`} data-testid="class-reminder-card">
            <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className={`${theme.text} font-semibold`}>Tuesday Class Reminders</p>
                <p className={`${theme.textMuted} text-sm`}>Automatic reminder emails go out to all registrants every Monday at 10:00 AM (starting 8/24). You can also send one manually.</p>
              </div>
              <Button
                onClick={sendClassReminders}
                disabled={sendingClassReminders}
                className={`${theme.buttonPrimary} rounded-full px-6`}
                data-testid="send-class-reminders-btn"
              >
                {sendingClassReminders ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                Send Reminder Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Check-In Controls */}
        {activeTab === "checkin" && (
          <Card className={`${theme.cardBg} border ${theme.cardBorder} mb-6`} data-testid="checkin-controls-card">
            <CardContent className="p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className={`${theme.text} font-semibold`}>Tuesday Class Check-In</p>
                  <p className={`${theme.textMuted} text-sm`}>Pick a class date, then tap each registrant who attended. Regulars (3+ classes) get a star.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="date"
                    value={checkinDate}
                    onChange={(e) => handleCheckinDateChange(e.target.value)}
                    className="bg-stone-700/50 border-amber-900/30 text-amber-100 w-44"
                    data-testid="checkin-date-input"
                  />
                  <span className={`${theme.textAccent} font-semibold whitespace-nowrap`} data-testid="checkin-count">
                    {attendance.length} / {(data.registrations || []).length} checked in
                  </span>
                </div>
              </div>
              <form onSubmit={addWalkin} className={`flex flex-wrap items-center gap-3 border-t ${theme.cardBorder} pt-4`} data-testid="walkin-form">
                <span className={`${theme.text} font-medium text-sm`}>Walk-in:</span>
                <Input
                  value={walkinName}
                  onChange={(e) => setWalkinName(e.target.value)}
                  placeholder="Name *"
                  required
                  className="bg-stone-700/50 border-amber-900/30 text-amber-100 w-48"
                  data-testid="walkin-name"
                />
                <Input
                  value={walkinPhone}
                  onChange={(e) => setWalkinPhone(e.target.value)}
                  placeholder="Phone (optional)"
                  className="bg-stone-700/50 border-amber-900/30 text-amber-100 w-44"
                  data-testid="walkin-phone"
                />
                <Button type="submit" disabled={addingWalkin || !walkinName.trim()} className={`${theme.buttonPrimary} rounded-full px-5`} data-testid="walkin-add-btn">
                  {addingWalkin ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Users className="w-4 h-4 mr-2" /> Add & Check In</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className={`w-8 h-8 ${theme.textAccent} animate-spin mx-auto mb-4`} />
                <p className={theme.textMuted}>Loading...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {activeTab === "camp" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Child</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Age</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Parent</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Phone</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Date</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.registrations || []).map((reg, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder} hover:${theme.highlight}`}>
                          <td className={`p-3 ${theme.text}`}>{reg.child_name || `${reg.child_first_name || ''} ${reg.child_last_name || ''}`.trim()}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.child_age}</td>
                          <td className={`p-3 ${theme.text}`}>{reg.parent_name || `${reg.parent_first_name || ''} ${reg.parent_last_name || ''}`.trim()}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.phone}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{new Date(reg.created_at).toLocaleDateString()}</td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              onClick={() => sendReminder(reg)}
                              disabled={sendingReminder === reg.id}
                              className={`${theme.buttonPrimary} rounded-full text-xs`}
                            >
                              {sendingReminder === reg.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3 h-3 mr-1" /> Remind
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {activeTab === "notify" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Parent</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Child's Age</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Signed Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.subscribers || []).map((sub, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder}`}>
                          <td className={`p-3 ${theme.text}`}>{sub.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{sub.parent_name || '—'}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{sub.child_age || '—'}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{new Date(sub.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {activeTab === "checkin" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Name</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Classes Attended</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>This Class</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.registrations || []).map((reg, i) => {
                        const checked = attendance.includes(reg.id);
                        const total = attendanceCounts[reg.id] || 0;
                        return (
                          <tr key={i} className={`border-b ${theme.cardBorder}`}>
                            <td className={`p-3 ${theme.text}`}>
                              {reg.name}
                              {reg.walk_in && <span className="ml-2 inline-block bg-sky-500/20 text-sky-400 text-xs font-semibold px-2 py-0.5 rounded-full">Walk-in</span>}
                              {total >= 3 && <span className="ml-2 inline-flex items-center gap-1 bg-amber-500/20 text-amber-400 text-xs font-semibold px-2 py-0.5 rounded-full"><Star className="w-3 h-3" /> Regular</span>}
                            </td>
                            <td className={`p-3 ${theme.textMuted}`}>{reg.email || "—"}</td>
                            <td className={`p-3 ${total === 0 ? 'text-red-400' : theme.textMuted}`}>{total === 0 ? "No classes yet" : `${total} ${total === 1 ? 'class' : 'classes'}`}</td>
                            <td className="p-3">
                              <Button
                                size="sm"
                                onClick={() => toggleCheckin(reg)}
                                disabled={togglingId === reg.id}
                                data-testid={`checkin-toggle-${i}`}
                                className={`rounded-full text-xs ${checked ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : `${theme.buttonPrimary}`}`}
                              >
                                {togglingId === reg.id ? (
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                ) : checked ? (
                                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Checked In</>
                                ) : (
                                  "Check In"
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {activeTab === "community" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Name</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Phone</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Adults</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Children</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Reminders</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.registrations || []).map((reg, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder}`}>
                          <td className={`p-3 ${theme.text}`}>{reg.name}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.phone}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.num_adults}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{reg.num_children}</td>
                          <td className="p-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${reg.reminders_on === false ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                              {reg.reminders_on === false ? "Opted Out" : "On"}
                            </span>
                          </td>
                          <td className={`p-3 ${theme.textMuted}`}>{new Date(reg.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {activeTab === "events" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Contact</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Event Type</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Date</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Location</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.bookings || []).map((booking, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder}`}>
                          <td className={`p-3 ${theme.text}`}>{booking.contact_name}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{booking.event_type}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{booking.event_date}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{booking.location}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{booking.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{booking.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {activeTab === "contact" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Name</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Subject</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Message</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.submissions || []).map((sub, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder}`}>
                          <td className={`p-3 ${theme.text}`}>{sub.name}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{sub.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{sub.subject}</td>
                          <td className={`p-3 ${theme.textMuted} max-w-xs truncate`}>{sub.message}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{new Date(sub.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {activeTab === "wesley" && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Name</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Phone</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Interested In</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.preregistrations || []).map((pre, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder}`}>
                          <td className={`p-3 ${theme.text}`}>{pre.name}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{pre.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{pre.phone || "—"}</td>
                          <td className="p-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full bg-amber-500/20 ${theme.textAccent}`}>{pre.interest}</span>
                          </td>
                          <td className={`p-3 ${theme.textMuted}`}>{new Date(pre.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {(activeTab === "homeschool" || activeTab === "corporate") && (
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${theme.cardBorder}`}>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Contact</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Email</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Phone</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Type</th>
                        <th className={`text-left p-3 ${theme.textAccent} font-semibold`}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.requests || []).map((req, i) => (
                        <tr key={i} className={`border-b ${theme.cardBorder}`}>
                          <td className={`p-3 ${theme.text}`}>{req.contact_name}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{req.email}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{req.phone}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{req.class_type}</td>
                          <td className={`p-3 ${theme.textMuted}`}>{new Date(req.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {((data.registrations?.length === 0 && (activeTab === "camp" || activeTab === "community")) ||
                  (data.bookings?.length === 0 && activeTab === "events") ||
                  (data.submissions?.length === 0 && activeTab === "contact") ||
                  (data.requests?.length === 0 && (activeTab === "homeschool" || activeTab === "corporate"))) && (
                  <div className="text-center py-12">
                    <p className={theme.textMuted}>No records found</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== CAMP PAYMENT PAGE ==============

const CampPaymentPage = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [weeks, setWeeks] = useState(4);
  const [pricingType, setPricingType] = useState("early");
  const [loading, setLoading] = useState(false);
  
  const childName = searchParams.get("child") || "Your Child";
  const registrationId = searchParams.get("registration_id") || "";

  const earlyBirdPrice = 125;
  const regularPrice = 150;
  const pricePerWeek = pricingType === "early" ? earlyBirdPrice : regularPrice;
  const totalAmount = pricePerWeek * weeks;

  const handlePayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/donate/checkout`, {
        amount: totalAmount,
        donor_name: childName,
        donor_email: null,
        message: `Summer Camp Payment - ${childName} - ${weeks} weeks - Registration ID: ${registrationId}`,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to process payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="camp-payment-page">
      <section className="py-24 bg-gradient-to-br from-stone-900 to-amber-950/30">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <CreditCard className={`w-16 h-16 ${theme.textAccent} mx-auto mb-4`} />
            <h1 className={`font-display text-4xl font-bold ${theme.text} mb-2`}>
              Summer Camp Payment
            </h1>
            <p className={`${theme.textMuted}`}>
              Complete payment for <strong className={theme.textAccent}>{childName}</strong>
            </p>
          </div>

          <Card className={`${theme.cardBg} border ${theme.cardBorder}`}>
            <CardContent className="p-8">
              {/* Camp Info */}
              <div className={`${theme.highlight} rounded-xl p-4 mb-6`}>
                <h3 className={`${theme.text} font-semibold mb-2`}>Camp Details</h3>
                <p className={`${theme.textMuted} text-sm`}>June 23 - July 16, 2026 (Tue, Wed, Thu)</p>
                <p className={`${theme.textMuted} text-sm`}>Showcase: July 17, 2026</p>
              </div>

              {/* Pricing Selection */}
              <div className="mb-6">
                <Label className={`${theme.text} mb-3 block`}>Select Pricing</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setPricingType("early")}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      pricingType === "early"
                        ? `border-amber-400 ${theme.highlight}`
                        : `${theme.cardBorder} hover:border-amber-400/50`
                    }`}
                  >
                    <p className={`${theme.textAccent} font-semibold`}>Early Bird</p>
                    <p className={`text-2xl font-bold ${theme.text}`}>${earlyBirdPrice}/week</p>
                    <p className={`${theme.textMuted} text-xs`}>Due by May 1, 2026</p>
                  </button>
                  <button
                    onClick={() => setPricingType("regular")}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      pricingType === "regular"
                        ? `border-amber-400 ${theme.highlight}`
                        : `${theme.cardBorder} hover:border-amber-400/50`
                    }`}
                  >
                    <p className={`${theme.textMuted} font-semibold`}>Regular</p>
                    <p className={`text-2xl font-bold ${theme.text}`}>${regularPrice}/week</p>
                    <p className={`${theme.textMuted} text-xs`}>After May 1, 2026</p>
                  </button>
                </div>
              </div>

              {/* Weeks Selection */}
              <div className="mb-6">
                <Label className={`${theme.text} mb-3 block`}>Number of Weeks</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((w) => (
                    <button
                      key={w}
                      onClick={() => setWeeks(w)}
                      className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                        weeks === w
                          ? `bg-gradient-to-r ${theme.primary} text-stone-900`
                          : `${theme.cardBg} ${theme.textMuted} border ${theme.cardBorder}`
                      }`}
                    >
                      {w} {w === 1 ? "Week" : "Weeks"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className={`bg-gradient-to-r ${theme.primary} rounded-xl p-6 mb-6 text-center`}>
                <p className="text-stone-800 font-medium">Total Amount</p>
                <p className="text-4xl font-bold text-stone-900">${totalAmount}</p>
                <p className="text-stone-700 text-sm">{weeks} week{weeks > 1 ? "s" : ""} × ${pricePerWeek}/week</p>
              </div>

              {/* Pay Button */}
              <Button
                onClick={handlePayment}
                disabled={loading}
                className={`w-full ${theme.buttonPrimary} py-6 text-lg rounded-xl`}
                data-testid="pay-now-btn"
              >
                {loading ? "Processing..." : (
                  <>
                    <CreditCard className="mr-2" /> Pay ${totalAmount} Now
                  </>
                )}
              </Button>

              <p className={`${theme.textMuted} text-center text-sm mt-4`}>
                Secure payment powered by Stripe
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

// ============== MAIN APP ==============

function App() {
  return (
    <ThemeProvider>
      <div className="App bg-stone-900 min-h-screen">
        <BrowserRouter>
          <ScrollToTop />
          <Navigation />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/classes" element={<ClassesPage />} />
            <Route path="/camp" element={<SummerCampPage />} />
            <Route path="/theater-program" element={<TheaterProgramPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/book-event" element={<BookEventPage />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/donate/success" element={<DonateSuccessPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/camp-payment" element={<CampPaymentPage />} />
          </Routes>
          <Footer />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
